window.FAFScrollLock = window.FAFScrollLock || (() => {
  const activeLocks = new Set();
  let scrollY = 0;
  let previousBodyStyles = null;
  let lockedBodyRect = null;
  let lockedHeader = null;
  let previousHeaderStyles = null;
  let lockedStickyElements = [];

  const pinHeader = () => {
    lockedHeader = document.querySelector('[data-site-header]');
    if (!lockedHeader) return;

    const rect = lockedHeader.getBoundingClientRect();

    previousHeaderStyles = {
      position: lockedHeader.style.position,
      top: lockedHeader.style.top,
      left: lockedHeader.style.left,
      right: lockedHeader.style.right,
      width: lockedHeader.style.width,
      maxWidth: lockedHeader.style.maxWidth,
      boxSizing: lockedHeader.style.boxSizing
    };

    /*
     * Preserve the header's exact pre-lock rectangle. Removing the page
     * scrollbar changes the layout viewport on desktop browsers; using the
     * captured width prevents a one-frame horizontal resize.
     */
    lockedHeader.style.position = 'fixed';
    lockedHeader.style.top = `${Math.max(0, rect.top)}px`;
    lockedHeader.style.left = `${rect.left}px`;
    lockedHeader.style.right = 'auto';
    lockedHeader.style.width = `100cqw`;
    lockedHeader.style.maxWidth = 'none';
    lockedHeader.style.boxSizing = 'border-box';
    window.FAFHeaderOffset?.sync?.();
  };

  const restoreHeader = () => {
    if (lockedHeader && previousHeaderStyles) {
      Object.assign(lockedHeader.style, previousHeaderStyles);
    }

    lockedHeader = null;
    previousHeaderStyles = null;
    window.FAFHeaderOffset?.queue?.();
  };

  const pinStickyElements = () => {
    lockedStickyElements = Array.from(
      document.querySelectorAll('[data-scroll-lock-pin]')
    ).map((element) => {
      const rect = element.getBoundingClientRect();
      const previousStyles = {
        position: element.style.position,
        top: element.style.top,
        right: element.style.right,
        bottom: element.style.bottom,
        left: element.style.left,
        width: element.style.width,
        height: element.style.height,
        maxWidth: element.style.maxWidth,
        boxSizing: element.style.boxSizing,
        margin: element.style.margin
      };

      element.style.position = 'fixed';
      element.style.top = `${rect.top}px`;
      element.style.right = 'auto';
      element.style.bottom = 'auto';
      element.style.left = `${rect.left}px`;
      element.style.width = `${rect.width}px`;
      element.style.height = `${rect.height}px`;
      element.style.maxWidth = 'none';
      element.style.boxSizing = 'border-box';
      element.style.margin = '0';

      return { element, previousStyles };
    });
  };

  const restoreStickyElements = () => {
    lockedStickyElements.forEach(({ element, previousStyles }) => {
      Object.assign(element.style, previousStyles);
    });

    lockedStickyElements = [];
  };

  const dispatchChange = (locked) => {
    window.dispatchEvent(
      new CustomEvent('faf:scroll-lock-change', {
        detail: {
          locked,
          scrollY
        }
      })
    );
  };

  const lock = (key = 'default') => {
    if (activeLocks.has(key)) return;

    if (activeLocks.size === 0) {
      scrollY = window.scrollY || window.pageYOffset || 0;
      lockedBodyRect = document.body.getBoundingClientRect();

      previousBodyStyles = {
        position: document.body.style.position,
        top: document.body.style.top,
        left: document.body.style.left,
        right: document.body.style.right,
        width: document.body.style.width,
        minWidth: document.body.style.minWidth,
        maxWidth: document.body.style.maxWidth,
        boxSizing: document.body.style.boxSizing,
        overflow: document.body.style.overflow
      };

      pinHeader();
      pinStickyElements();

      document.documentElement.classList.add('overflow-hidden');
      document.body.classList.add('overflow-hidden');

      /*
       * Do not switch the body to width: 100%. Once the vertical scrollbar is
       * removed, 100% is wider than the pre-lock layout viewport. Keeping the
       * captured body rectangle prevents collection grids, headers and sticky
       * controls from narrowing/widening during drawer transitions.
       */
      const bodyWidth = Math.max(
        0,
        lockedBodyRect?.width || document.documentElement.clientWidth
      );

      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = `${lockedBodyRect?.left || 0}px`;
      document.body.style.right = 'auto';
      document.body.style.width = `${bodyWidth}px`;
      document.body.style.minWidth = `${bodyWidth}px`;
      document.body.style.maxWidth = `${bodyWidth}px`;
      document.body.style.boxSizing = 'border-box';
      document.body.style.overflow = 'hidden';
    }

    activeLocks.add(key);

    if (activeLocks.size === 1) {
      dispatchChange(true);
    }
  };

  const unlock = (key = 'default') => {
    activeLocks.delete(key);

    if (activeLocks.size > 0 || !previousBodyStyles) return;

    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');

    Object.assign(document.body.style, previousBodyStyles);
    previousBodyStyles = null;
    lockedBodyRect = null;

    restoreHeader();
    restoreStickyElements();

    window.scrollTo(0, scrollY);
    dispatchChange(false);
  };

  return {
    lock,
    unlock,
    isLocked: () => activeLocks.size > 0
  };
})();

window.FAFQRCode = window.FAFQRCode || (() => {
  const source = 'https://cdn.jsdelivr.net/npm/qrcodejs/qrcode.min.js';
  let loadPromise = null;

  const load = () => {
    if (window.QRCode) return Promise.resolve(window.QRCode);
    if (loadPromise) return loadPromise;

    loadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[data-faf-qrcode][src="${source}"]`);
      const script = existing || document.createElement('script');

      const finish = () => window.QRCode
        ? resolve(window.QRCode)
        : reject(new Error('QR code library loaded without exposing QRCode.'));

      script.addEventListener('load', finish, { once: true });
      script.addEventListener('error', () => reject(new Error('Unable to load the QR code library.')), { once: true });

      if (!existing) {
        script.src = source;
        script.async = true;
        script.dataset.fafQrcode = 'true';
        document.head.appendChild(script);
      }
    }).catch((error) => {
      loadPromise = null;
      throw error;
    });

    return loadPromise;
  };

  const render = async (holder, options = {}) => {
    if (!holder) throw new Error('A QR code holder is required.');
    const QRCodeConstructor = await load();
    holder.replaceChildren();

    const levelName = options.correctLevel || 'M';
    const correctLevel = QRCodeConstructor.CorrectLevel?.[levelName]
      ?? QRCodeConstructor.CorrectLevel?.M;

    return new QRCodeConstructor(holder, {
      ...options,
      correctLevel
    });
  };

  return { load, render };
})();

/**
 * Change desktop header height on scroll
 */

function updateHeaderScrollState() {
  /*
   * Fixing the body can temporarily make browsers report scrollY as zero.
   * Keep the current expanded/compact header state while a drawer is open.
   */
  if (window.FAFScrollLock?.isLocked()) return;

  const scrollThreshold = window.innerWidth <= 768 ? 38 : 100;

  document.body.classList.toggle(
    'body-scrolled',
    window.scrollY >= scrollThreshold
  );
}

document.addEventListener(
  'scroll',
  updateHeaderScrollState,
  { passive: true }
);

window.addEventListener(
  'resize',
  updateHeaderScrollState,
  { passive: true }
);

window.addEventListener('faf:scroll-lock-change', (event) => {
  if (!event.detail?.locked) {
    updateHeaderScrollState();
  }
});

updateHeaderScrollState();

window.FAFLayout =
  window.FAFLayout || {};

window.FAFLayout.isStickyDesktopPdp =
  window.FAFLayout.isStickyDesktopPdp ||
  function isStickyDesktopPdp() {
    return (
      window.FAFThemeConfig?.pageType ===
        'product' &&
      window.matchMedia(
        '(min-width: 1024px)'
      ).matches
    );
  };

let lazyPanelLockMode = null;

function toggleBodyScroll(shouldLock) {
  if (shouldLock) {
    if (lazyPanelLockMode) return;

    if (
      window.FAFLayout
        .isStickyDesktopPdp()
    ) {
      lazyPanelLockMode =
        'product-desktop-passive';

      return;
    }

    lazyPanelLockMode = 'global';

    window.FAFScrollLock.lock(
      'lazy-panel'
    );

    document.body.classList.remove(
      'lg:overflow-auto'
    );

    return;
  }

  if (
    lazyPanelLockMode === 'global'
  ) {
    window.FAFScrollLock.unlock(
      'lazy-panel'
    );

    document.body.classList.add(
      'lg:overflow-auto'
    );
  }

  lazyPanelLockMode = null;
}

function createPushCartItem(key, quantity) {
  return {
    key: key,
    disabled: false,
    quantityMessageBold: false,
    quantity: quantity,

    flashQuantityMessage() {
      this.quantityMessageBold = true;

      setTimeout(() => {
        this.quantityMessageBold = false;
      }, 500);
    },

    change(formData, onComplete) {
      this.disabled = true;

      Alpine.store('cart').change(
        formData,
        onComplete ? onComplete : () => {
          this.disabled = false;
        }
      );
    },

    setQuantity(qty) {
      this.change({
        id: this.key,
        quantity: qty,
        sections: 'push-cart,cart-count'
      });
    }
  };
}

document.addEventListener('alpine:init', () => {
  /**
   * Push cart item
   
  Alpine.data('pushCartItem', (key, quantity) => ({
    key: key,
    disabled: false,
    quantityMessageBold: false,
    quantity: quantity,

    flashQuantityMessage() {
      this.$data.quantityMessageBold = true;
      setTimeout(() => {
        this.$data.quantityMessageBold = false;
      }, 500);
    },

    change: function(formData, onComplete) {
      this.disabled = true;
      Alpine.store('cart').change(formData, onComplete ? onComplete : () => this.disabled = false);
    },

    setQuantity: function(qty) {
      this.change({
        'id': this.key,
        'quantity': qty,
        'sections': 'push-cart,cart-count'
      });
    },

  }));
*/
  Alpine.data('cartGiftWrap', (key, name, message, variant) => ({
    locked: false,
    key: key,
    variant: variant,
    name: name,
    message: message,
    errorMessage: '',
    addGiftWrap() {
      if(this.$root.querySelectorAll('.js-giftwrap-checkbox:checked').length == 0) {
        return this.setError('At least one item needs to be checked for giftwrap');
      }

      if(!this.name) {
        return this.setError('Please fill in a name in the "To:" box above');
      }

      this.locked = true;

      if(this.key) {
        Alpine.store('cart').change({
          'sections': 'push-cart',
          'id': this.key,
          'properties': {
            "GM To": this.name,
            "GM": this.message
          },
        }, (error) => {
          this.locked = false;

          if (error) {
            this.setError(error.message || 'Gift wrapping could not be updated.');
            return;
          }

          Alpine.store('cart').mode = 'cart';
        });
      } else {
        Alpine.store('cart').addVariant(
          this.variant,
          (error) => {
            this.locked = false;

            if (error) {
              this.setError(error.message || 'Gift wrapping could not be added.');
              return;
            }

            Alpine.store('cart').mode = 'cart';
          },
          {
            "GM To": this.name,
            "GM": this.message
          },
          null,
          null,
          {
            source: 'gift_wrap'
          }
        );
      }
    },
    toggleItemGiftwrap(index, current) {
      this.locked = true;
      Alpine.store('cart').change({
        'sections': 'push-cart',
        'line': index,
        'properties': {
          '_wrapped': current == 'true' ? 'false' : 'true'
        },
      }, (error) => {
        this.locked = false;

        if (error) {
          this.setError(error.message || 'Gift wrapping could not be updated.');
        }
      });
    },
    setError(message) {
      this.errorMessage = message;
      setTimeout(() => this.errorMessage = '', 3000);
    }
  }));



  /**
   * Carousel, with filter option
   */
  Alpine.data('carousel', (loaded = true, tab = '') => ({
    tag: '',
    tab: tab,
    leftActive: false,
    loaded: loaded,
    rightActive: true,
    asyncItems: [],
    empty: false,
    percentageScroll: 0,

    init() {
      this.updateArrows();
    },

    scroll(direction) {
      let width = this.$refs.slider.offsetWidth;
      let currentPosition = this.$refs.slider.scrollLeft;
      this.$refs.slider.scrollTo({ left: direction == 'left' ? currentPosition - width : currentPosition + width, behavior: 'smooth' });
    },

    updateArrows() {
      const el = this.getActiveSlider();
      if(!el){
        this.leftActive=false;
        this.rightActive=false;
        return;
      }

      const max = Math.max(0, (el.scrollWidth || 0) - (el.clientWidth || 0));
      const x = el.scrollLeft || 0;
      this.leftActive  = x > 0;
      this.rightActive = x < (max - 1);
    },

    filter(tag) {
      if(this.$data.tag == tag) {
        return;
      }

      this.$data.tag = tag;
      this.$refs.slider.querySelectorAll('[data-tags]').forEach(el => {
        el.classList.remove('hidden');
        let tags = JSON.parse(el.dataset.tags);
        if(tags.indexOf(tag) != -1 || tag == '') {
          el.classList.remove('hidden');
        } else {
          el.classList.add('hidden');
        }
      });

      this.$refs.slider.scrollTo({ left: 0, behavior: 'smooth' });
    },

    load(url, parser) {
      fetch(url)
        .then(response => { return response.json();})
        .then(response => {
          this.$data.asyncItems = parser(response).filter(p => p);
          this.$data.loaded = true;
          if(this.$data.asyncItems.length == 0) {
            this.$data.empty = true;
          }
        });
    },
    scrollTo(scrollPercentage) {
      this.$refs.slider.scrollTo({ left: scrollPercentage * this.$refs.slider.scrollWidth, behavior: 'smooth' });
    },

    /**
     * Multi tab slider functions
     **/

    // When tab is set, use that
    getActiveSlider(){
      return this.tab ? document.getElementById(this.tab + '-slider') : this.$refs.slider;
    },

    scrollActive(dir){
      const el = this.getActiveSlider();
      if(!el) return;
      const step = Math.round(el.clientWidth * 0.9); // near-viewport step
      el.scrollBy({ left: dir === 'right' ? step : -step, behavior: 'smooth' });
      setTimeout(() => this.updateArrows(), 150);
    },

  }));

Alpine.data('mobileSizeGrid', (config = {}) => ({
  quickAddToBag: false,
  added: false,
  variantId: '',
  loading: false,
  error: '',
  productId: Number(config.productId || 0),
  productLimit: Number(config.productLimit || 0),
  variantInventory: config.variants || {},

  init() {
    Alpine.store('cart')?.registerProductRule(
      this.productId,
      this.productLimit,
      this.variantInventory
    );
  },

  quantityInCart(variantId) {
    return Alpine.store('cart').quantityFor(variantId);
  },

  quantityForProduct() {
    if (!this.productId) return 0;
    return Alpine.store('cart').quantityForProduct(this.productId);
  },

  inventoryLimitForVariant(variantId) {
    const inventory = this.variantInventory[String(Number(variantId || 0))];
    if (!inventory) return null;

    if (!inventory.tracksInventory || inventory.inventoryPolicy === 'continue') {
      return null;
    }

    return Math.max(0, Number(inventory.inventoryQuantity || 0));
  },

  get qtyInStock() {
    const numericVariantId = Number(this.variantId || 0);
    if (!numericVariantId) return null;

    const inventoryLimit = this.inventoryLimitForVariant(numericVariantId);
    if (inventoryLimit === null) return -1;

    return Math.max(0, inventoryLimit - this.quantityInCart(numericVariantId));
  },

  isProductAtLimit() {
    return this.productLimit > 0 && this.quantityForProduct() >= this.productLimit;
  },

  isVariantAtLimit(variantId) {
    const numericVariantId = Number(variantId || 0);
    const inventory = this.variantInventory[String(numericVariantId)];

    if (!inventory || !inventory.available) return true;
    if (this.isProductAtLimit()) return true;

    const inventoryLimit = this.inventoryLimitForVariant(numericVariantId);
    if (inventoryLimit === null) return false;

    return this.quantityInCart(numericVariantId) >= inventoryLimit;
  },

  selectVariant(variantId) {
    const numericVariantId = Number(variantId || 0);
    if (!numericVariantId || this.isVariantAtLimit(numericVariantId)) return;

    this.variantId = Number(this.variantId) === numericVariantId ? '' : numericVariantId;
    this.error = '';
  },

  add() {
    if (!this.variantId || this.isVariantAtLimit(this.variantId) || this.loading) return;
    this.addVariant(this.variantId);
  },

  addVariant(id) {
    const numericVariantId = Number(id || 0);
    if (!numericVariantId || this.loading || this.isVariantAtLimit(numericVariantId)) return;

    this.loading = true;
    this.error = '';
    const quickAddContext =
      window.FAFQuickAddContext || {};

    const analyticsSource =
      quickAddContext.context ===
      'wishlist'
        ? 'wishlist_move_to_cart'
        : 'mobile_quick_add';

    Alpine.store('cart').addVariant(
      numericVariantId,
      (error) => {
        this.loading = false;

        if (error) {
          this.error = error.message || 'This item could not be added. Please try again.';
          return;
        }

        window.dispatchEvent(new CustomEvent('popup-add:added', {
          detail: { variantId: numericVariantId }
        }));

        this.added = true;
        setTimeout(() => {
          this.added = false;
          this.$dispatch('close-popup-add');
        }, 850);
      },
      null,
      null,
      'quick',
      {
        productId:
          this.productId,

        productLimit:
          this.productLimit,

        inventoryLimit:
          this.inventoryLimitForVariant(
            numericVariantId
          ),

        source:
          analyticsSource
      }
    );
  }
}));



  /**
   * Popup for mobile quick add
   */
Alpine.data('popupAdd', () => ({
  closing: false,
  closeTimer: null,
  open: false,
  loaded: false,
  error: false,
  lastFocusedElement: null,
  requestController: null,
  requestId: 0,
  addedHandler: null,
  markupCache: {},
  cacheDuration: 60000,

  context: '',
  wishlistHandle: '',
  wishlistProductId: 0,

init() {
  this.addedHandler = (event) => {
    this.handleAdded(event);
  };

  window.addEventListener(
    'popup-add:added',
    this.addedHandler
  );
},

  cancelClose() {
  if (this.closeTimer) {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  this.closing = false;
},

finishClose() {
  this.cancelClose();

  this.requestId += 1;

  if (this.$refs.dialog?.open) {
    this.$refs.dialog.close();
  }

  window.FAFScrollLock.unlock('quick-add');

  this.loaded = false;
  this.error = false;

  this.requestController?.abort();
  this.requestController = null;

  this.resetContext();

  if (this.lastFocusedElement?.isConnected) {
    requestAnimationFrame(() => {
      this.lastFocusedElement?.focus({
        preventScroll: true
      });
    });
  }

  this.lastFocusedElement = null;
},

destroy() {
  this.cancelClose();

  this.requestId += 1;

  this.requestController?.abort();
  this.requestController = null;

  if (this.$refs.dialog?.open) {
    this.$refs.dialog.close();
  }

  window.FAFScrollLock.unlock(
    'quick-add'
  );

  if (this.addedHandler) {
    window.removeEventListener(
      'popup-add:added',
      this.addedHandler
    );

    this.addedHandler = null;
  }
},

    resetContext() {
      this.context = '';
      this.wishlistHandle = '';
      this.wishlistProductId = 0;

      window.FAFQuickAddContext =
        null;
    },

  focusableElements() {
    if (!this.$refs.panel) return [];

    return Array.from(
      this.$refs.panel.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => {
      return (
        element.offsetWidth > 0 ||
        element.offsetHeight > 0
      );
    });
  },

  focusPanel() {
    const focusable =
      this.focusableElements();

    (
      focusable[0] ||
      this.$refs.panel
    )?.focus();
  },

  close() {
  if (
    !this.open ||
    this.closing
  ) {
    return;
  }

  this.closing = true;
  this.open = false;

  /*
   * Allow the bottom-sheet leave transition
   * to finish before removing the dialog
   * from the browser top layer.
   */
  this.closeTimer =
    window.setTimeout(
      () => {
        this.finishClose();
      },
      340
    );
},

  reveal($event) {
  const detail = $event.detail || {};
  const handle = String(
    detail.handle || ''
  ).trim();

  if (!handle) return;

  this.cancelClose();

  const requestId =
    ++this.requestId;

  this.requestController?.abort();
  this.requestController = null;

  this.lastFocusedElement =
    document.activeElement;

  window.FAFQuickAddContext = {
    context: String(
      detail.context || ''
    ),

    productId: Number(
      detail.productId || 0
    ),

    wishlistHandle: String(
      detail.wishlistHandle ||
      handle ||
      ''
    )
  };

  this.context =
    detail.context || '';

  this.wishlistHandle =
    detail.wishlistHandle ||
    handle;

  this.wishlistProductId =
    detail.productId || 0;

  this.loaded = false;
  this.error = false;

  /*
   * Make Alpine render the panel before promoting
   * the native dialog to the top layer.
   */
  this.open = true;

  this.$nextTick(() => {
    if (
      !this.open ||
      requestId !== this.requestId
    ) {
      return;
    }

    const dialog =
      this.$refs.dialog;

    if (
      !dialog ||
      typeof dialog.showModal !==
        'function'
    ) {
      this.open = false;
      return;
    }

    try {
      if (!dialog.open) {
        dialog.showModal();
      }
    } catch (error) {
      console.error(
        'Quick-add dialog could not be opened.',
        error
      );

      this.open = false;
      return;
    }

    window.FAFScrollLock.lock(
      'quick-add'
    );

    requestAnimationFrame(() => {
      this.focusPanel();
    });
  });

  const cached =
    this.markupCache[handle];

    if (
      cached &&
      Date.now() - cached.cachedAt <
        this.cacheDuration
    ) {
      if (
        !this.open ||
        requestId !== this.requestId
      ) {
        return;
      }

      this.$refs.content.innerHTML =
        cached.markup;

      window.FAFSections?.enhance?.(
        this.$refs.content,
        { source: 'quick-add-cache' }
      );

      this.$nextTick(() => {
        if (
          !this.open ||
          requestId !== this.requestId
        ) {
          return;
        }

        this.loaded = true;
        this.focusPanel();
      });

      return;
    }

    this.requestController =
      new AbortController();

    fetch(
      `${window.Shopify.routes.root}products/${handle}?view=mobile-quick-add`,
      {
        signal:
          this.requestController.signal
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Quick add returned ${response.status}`
          );
        }

        return response.text();
      })
      .then((markup) => {
        if (
          !this.open ||
          requestId !== this.requestId
        ) {
          return;
        }

        this.markupCache[handle] = {
          markup,
          cachedAt: Date.now()
        };

        this.$refs.content.innerHTML =
          markup;

        window.FAFSections?.enhance?.(
          this.$refs.content,
          { source: 'quick-add-fetch' }
        );

        this.$nextTick(() => {
          if (
            !this.open ||
            requestId !== this.requestId
          ) {
            return;
          }

          this.loaded = true;
          this.focusPanel();
        });
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }

        if (
          !this.open ||
          requestId !== this.requestId
        ) {
          return;
        }

        this.error = true;
        this.loaded = true;
      });
  },

    handleAdded(event) {
      if (
        this.context !== 'wishlist' ||
        !this.wishlistHandle
      ) {
        return;
      }

      const variantId =
        Number(
          event.detail?.variantId || 0
        );

      const removed =
        Alpine.store('wishlist').remove(
          this.wishlistHandle
        );

      if (!removed) return;

      window.FAFAnalytics?.custom(
        'faf_remove_from_wishlist',
        {
          product_id:
            String(
              this.wishlistProductId ||
              ''
            ),

          variant_id:
            String(
              variantId || ''
            ),

          source:
            'wishlist_drawer',

          reason:
            'move_to_cart'
        }
      );
    }
}));

  /**
   * Lazy carousel - wrapper around carousel with fetch collection
   */
   Alpine.data('lazyCarousel', (handle) => ({
    lazyMarkup: '',
    lazyLoaded: false,
    handle: handle,
    load() {
      fetch(`${window.Shopify.routes.root}collections/${encodeURIComponent(this.handle)}?view=carousel&limit=20`)
        .then(response => response.text())
        .then(text => {
          this.$data.lazyMarkup = new DOMParser().parseFromString(text, 'text/html').getElementById('carousel-content').innerHTML;
          this.$data.lazyLoaded = true;
        });
    }
  }));

  /**
   * Tassel upsell - global as included in push cart
   */
  Alpine.data('tasselUpsell', (tasselImage) => ({
    selected: '',
    adding: false,
    price: '',
    tasselImage: tasselImage,
    add() {
      this.$data.adding = true;
      Alpine.store('cart').addVariant(
        this.selected,
        () => {
          this.$data.adding = false;
        },
        null,
        null,
        null,
        {
          source: 'cart_upsell'
        }
      );
    }
  }));

  /**
   * Alibi in push cart
   */
Alpine.data(
  'alibi',
  (id = '') => ({
    alibiOpen: false,
    selection: '',
    id,

    reset() {
      this.alibiOpen = false;
      this.selection = '';
    },

    close() {
      this.reset();
      this.$root.open = false;
    },

    save() {
      if (this.selection === 'gift') {
        Alpine.store('cart').addVariant(
          39337899884616,
          null,
          null,
          null,
          null,
          {
            source: 'cart_alibi_gift'
          }
        );
      }

      if (this.selection === 'comp') {
        Alpine.store('cart').addVariant(
          39429425266760,
          null,
          null,
          null,
          null,
          {
            source: 'cart_alibi_complimentary'
          }
        );
      }
    },

    remove() {
      if (!this.id) return;

      Alpine.store('cart').change({
        id: this.id,
        quantity: 0,
        sections: 'push-cart,cart-count'
      });
    }
  })
);

  /**
   * Add discount via push cart
   */
  Alpine.data('pushDiscounts', (codes, open) => ({
    codes: codes,
    open: open,
    locked: false,
    codeToAdd: '',
    error: '',

    setError(msg, timeout) {
      this.error = msg;
      setTimeout(() => this.error = '', timeout ?? 1500);
    },

    applyNew() {
      if(!this.codeToAdd) {
        this.setError('Discount code empty');
        return;
      }
      let newCodes = [...this.codes, this.codeToAdd];
      this.updateDiscount(newCodes.join(','));
    },

    removeDiscount(code) {
      let newCodes = this.codes.filter(c => c !== code);
      this.updateDiscount(newCodes.join(','));
    },

    updateDiscount(codes) {

      this.locked = true;

      const body = new URLSearchParams({
        discount: codes,
        sections: 'push-cart,cart-count'
      });

      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          Accept: 'application/json'
        },
        body: body.toString()
      })
        .then(async (response) => {
          const payload = await response.json().catch(() => ({}));

          if (!response.ok || payload.status) {
            throw new Error(
              payload.description ||
              payload.message ||
              'The discount could not be updated.'
            );
          }

          return payload;
        })
        .then(response => {

          if(this.codeToAdd) {
            let added = response.discount_codes.filter(c => c.code === this.codeToAdd);
            if(added.length && !added[0].applicable) {
              this.setError("Failed to add code '" + this.codeToAdd + "'", 3000);
              return;
            }
          }

          applyCartResponse(response);
        })
        .catch((error) => {
          this.setError(error.message || 'The discount could not be updated.', 3000);
        })
        .finally(() => {
          this.locked = false;
        });
    }
  }));


    Alpine.store('profile', {
    birthday: '',
    sms_sub: false,
    appLoggedIn: false,
    emailSub: false,
    showProgress: true,

    init(data = {}) {
      this.birthday = data.birthday || '';
      this.sms_sub = !!data.sms_sub;
      this.appLoggedIn = !!data.appLoggedIn;
      this.emailSub = !!data.emailSub;
      this.showProgress = !!data.showProgress;
    }
  });

  window.fafShareModal = window.fafShareModal || function (config = {}) {
    const defaults = {
      title: config.title || document.title,
      description: config.description || '',
      url: config.url || window.location.href,
      context: config.context || 'page'
    };

    return {
      open: false,
      copied: false,
      canNativeShare: false,
      title: defaults.title,
      description: defaults.description,
      url: defaults.url,
      context: defaults.context,
      returnFocusElement: null,
      copiedTimer: null,
      fallbackDocumentOverflow: '',
      lockMode: null,
      scrollLockKey: 'share-modal',
      urls: {
        whatsapp: 'https://wa.me/?text=[DESCRIPTION]%20[URL]',
        mail: 'mailto:?subject=[TITLE]&body=[DESCRIPTION]%0A%0A[URL]',
        facebook: 'https://www.facebook.com/sharer/sharer.php?u=[URL]',
        x: 'https://x.com/intent/post?text=[DESCRIPTION]&url=[URL]',
        pinterest: 'https://pinterest.com/pin/create/button/?url=[URL]&description=[DESCRIPTION]'
      },

      init() {
        this.canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
      },

      resetShareData(payload = {}) {
        this.title = payload.title || defaults.title;
        this.description = payload.description || defaults.description;
        this.url = payload.url || defaults.url;
        this.context = payload.context || defaults.context;
        this.copied = false;
      },

      async launch(detail, trigger) {
        const payload = typeof detail === 'string' ? { url: detail } : (detail || {});
        this.resetShareData(payload);
        this.returnFocusElement = trigger instanceof HTMLElement ? trigger : document.activeElement;

        const prefersNativeShare = this.canNativeShare && (
          window.matchMedia('(pointer: coarse)').matches ||
          navigator.maxTouchPoints > 0
        );

        if (prefersNativeShare) {
          try {
            await this.shareNative();
            return;
          } catch (error) {
            if (error && error.name === 'AbortError') return;
          }
        }

        this.openModal();
      },

        isDesktopProductPage() {
          return Boolean(
            window.FAFLayout
              ?.isStickyDesktopPdp?.()
          );
        },

      lockPage() {
        /*
         * The PDP media column is sticky and contains its own vertical
         * scroller. Fixing the body here makes the browser recalculate
         * that gallery, which visibly moves the active product image.
         *
         * The full-screen share backdrop already blocks pointer access
         * to the page, so desktop PDPs can remain positionally passive.
         */
        if (this.isDesktopProductPage()) {
          this.lockMode = 'product-desktop-passive';
          return;
        }

        if (window.FAFScrollLock?.lock) {
          this.lockMode = 'global';
          window.FAFScrollLock.lock(this.scrollLockKey);
          return;
        }

        this.lockMode = 'fallback';
        this.fallbackDocumentOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
      },

      unlockPage() {
        if (this.lockMode === 'global' && window.FAFScrollLock?.unlock) {
          window.FAFScrollLock.unlock(this.scrollLockKey);
        } else if (this.lockMode === 'fallback') {
          document.documentElement.style.overflow = this.fallbackDocumentOverflow;
        }

        this.lockMode = null;
      },

      openModal() {
        if (this.open) return;

        this.open = true;
        this.lockPage();

        this.$nextTick(() => this.$refs.closeButton?.focus({ preventScroll: true }));
      },

      close(restoreFocus = true) {
        if (!this.open) return;

        this.open = false;
        this.copied = false;

        this.unlockPage();

        if (this.copiedTimer) {
          window.clearTimeout(this.copiedTimer);
          this.copiedTimer = null;
        }

        if (restoreFocus && this.returnFocusElement?.focus) {
          this.$nextTick(() => this.returnFocusElement.focus({ preventScroll: true }));
        }
      },

      destroy() {
        this.close(false);
      },

      shareUrl(template) {
        const replacements = {
          '[TITLE]': encodeURIComponent(this.title || ''),
          '[DESCRIPTION]': encodeURIComponent(this.description || this.title || ''),
          '[URL]': encodeURIComponent(this.url || window.location.href)
        };

        return Object.keys(replacements).reduce(
          (result, token) => result.split(token).join(replacements[token]),
          template
        );
      },

      async shareNative() {
        await navigator.share({
          title: this.title,
          text: this.description,
          url: this.url
        });
        this.track('native');
      },

      async openNativeShare() {
        try {
          await this.shareNative();
          this.close();
        } catch (error) {
          if (!error || error.name !== 'AbortError') {
            this.copied = false;
          }
        }
      },

      async copy() {
        let copiedSuccessfully = false;

        if (navigator.clipboard && window.isSecureContext) {
          try {
            await navigator.clipboard.writeText(this.url);
            copiedSuccessfully = true;
          } catch (error) {
            copiedSuccessfully = false;
          }
        }

        if (!copiedSuccessfully && this.$refs.copyUrl) {
          try {
            this.$refs.copyUrl.focus();
            this.$refs.copyUrl.select();
            copiedSuccessfully = document.execCommand('copy');
          } catch (error) {
            copiedSuccessfully = false;
          }
        }

        if (!copiedSuccessfully) return;

        this.copied = true;
        this.track('copy');

        if (this.copiedTimer) window.clearTimeout(this.copiedTimer);
        this.copiedTimer = window.setTimeout(() => {
          this.copied = false;
          this.copiedTimer = null;
        }, 2200);
      },

      trapFocus(event) {
        if (event.key !== 'Tab' || !this.$refs.dialog) return;

        const focusable = Array.from(
          this.$refs.dialog.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((element) => element.offsetParent !== null);

        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      },

      track(method) {
        window.FAFAnalytics?.custom(
          'faf_content_shared',
          {
            method,

            content_type:
              this.context ||
              'page',

            page_path:
              window.location.pathname
          }
        );
      }
    };
  };

  Alpine.data('search', (section_id) => ({
    section_id,
    show: false,
    searched: false,
    loading: false,
    error: false,
    term: '',
    searchTimer: null,
    searchResultsInstance: null,
    searchSuggestions: null,
    lastNoResultsTerm: '',
    recentSearches: [],

    loadRecentSearches() {
      let stored = [];

      try {
        stored = JSON.parse(
          window.localStorage.getItem('faf:recent-searches:v1') || '[]'
        );
      } catch (error) {
        stored = [];
      }

      const seen = new Set();

      this.recentSearches = (Array.isArray(stored) ? stored : [])
        .filter((value) => typeof value === 'string')
        .map((value) => value.trim())
        .filter((value) => {
          const key = value.toLowerCase();

          if (value.length < 2 || seen.has(key)) return false;

          seen.add(key);
          return true;
        })
        .slice(0, 6);
    },
    results: {
      products: { hits: [], total: 0 },
      suggestions: { hits: [] },
      pages: { hits: [] },
      collections: { hits: [] }
    },

    init() {
      this.$watch('show', (visible) => {
        document.body.classList.toggle('faf-search-open', visible);
        if (visible) {
          window.FAFScrollLock.lock('search');
          window.dispatchEvent(new CustomEvent('search-opened'));
        } else {
          window.FAFScrollLock.unlock('search');
        }
      });

      this.$watch('term', () => this.queueSearch());
      onTagalysReady(() => this.startTagalys());
    },

    destroy() {
      clearTimeout(this.searchTimer);
      window.FAFScrollLock.unlock('search');
      document.body.classList.remove('faf-search-open');
    },

    openSearch(query = '') {
      this.show = true;
      const nextTerm = String(query || '');
      if (this.term === nextTerm) this.queueSearch();
      else this.term = nextTerm;
    },

    closeSearch() {
      this.show = false;
      this.loading = false;
      clearTimeout(this.searchTimer);
    },

    resetResults() {
      this.searched = false;
      this.loading = false;
      this.error = false;
      this.results = {
        products: { hits: [], total: 0 },
        suggestions: { hits: [] },
        pages: { hits: [] },
        collections: { hits: [] }
      };
      const target = document.getElementById('instant-product-results');
      if (target) target.innerHTML = '';
    },

    queueSearch() {
      clearTimeout(this.searchTimer);
      const query = this.term.trim();

      if (!query) {
        this.resetResults();
        return;
      }

      if (query.length < 2) {
        this.searched = false;
        this.loading = false;
        this.error = false;
        return;
      }

      this.loading = true;
      this.error = false;
      this.searchTimer = setTimeout(() => this.search(), 160);
    },

    startTagalys() {
      if (this.searchSuggestions || !this.$root.isConnected) return;
      this.searchResultsInstance = Tagalys.APIConnector.SearchResults.new({
        onSuccess: () => {}
      });

      this.searchSuggestions = Tagalys.APIConnector.SearchSuggestions.new({
        params: {
          request: {
            searches: { limit: 6 },
            products: { limit: 12 },
            collections: { limit: 4 },
            pages: { limit: 4 }
          }
        },
        onSuccess: (response) => {
          this.loading = false;
          this.error = false;
          this.searched = true;
          this.results = {
            products: {
              total: response?.metadata?.products?.total || 0,
              hits: response?.products || []
            },
            suggestions: {
              hits: response?.queries?.find((query) => query.section_id === 'searches')?.items || []
            },
            pages: {
              hits: response?.queries?.find((query) => query.section_id === 'pages')?.items || []
            },
            collections: {
              hits: response?.queries?.find((query) => query.section_id === 'collections')?.items || []
            }
          };
          const completedSearchTerm =
            this.term.trim();

          const normalisedSearchTerm =
            completedSearchTerm.toLowerCase();

          if (
            this.results.products.total === 0 &&
            normalisedSearchTerm &&
            normalisedSearchTerm !==
              this.lastNoResultsTerm
          ) {
            this.lastNoResultsTerm =
              normalisedSearchTerm;

            window.FAFAnalytics?.custom(
              'faf_search_no_results',
              {
                search_term:
                  completedSearchTerm,
                search_surface:
                  'instant'
              }
            );
          } else if (
            this.results.products.total > 0
          ) {
            this.lastNoResultsTerm = '';
          }

          const target = document.getElementById('instant-product-results');
          if (target) target.innerHTML = '';

          const idList = this.results.products.hits.map((product) => `id:${product.id}`).join(' OR ');
          if (idList) {
            const markupSearchTerm = this.term.trim();

            const url =
              `${window.Shopify.routes.root}search?q=${encodeURIComponent(idList)}` +
              '&type=product&section_id=faf-instant-search-results';

            sectionReplace(
              url,
              'instant-product-results',
              'remote-instant-search'
            )
              .then((target) => {
                if (!target) {
                  throw new Error('Instant-search product markup was missing.');
                }

                window.FAFAnalytics?.viewSearchResults(target, {
                  searchTerm: markupSearchTerm,
                  list: 'instant_search'
                });
              })
              .catch((error) => {
                console.warn('Instant-search products could not be loaded.', error);

                if (this.term.trim() !== markupSearchTerm) return;

                this.loading = false;
                this.searched = false;
                this.error = true;
              });
          }
        },
        onFailure: () => {
          this.loading = false;
          this.searched = false;
          this.error = true;
        }
      });

      if (this.term.trim().length >= 2) this.queueSearch();
    },

    search() {
      const query = this.term.trim();

      if (query.length < 2) {
        this.loading = false;
        return;
      }

      if (!this.searchSuggestions?.helpers) {
        this.loading = false;
        this.searched = false;
        this.error = true;

        onTagalysReady(() => this.startTagalys());
        return;
      }

      this.searchSuggestions.helpers.updateQuery(query);
    },

    get viewAllUrl() {
      const params = new URLSearchParams({
        q: this.term.trim(),
        type: 'product',
        'options[prefix]': 'last'
      });

      return `${window.routes.search}?${params.toString()}`;
    },

    getMeta(metadata, namespace, key, formatter) {
      if (metadata?.[namespace]?.[key]) {
        return formatter ? formatter(metadata[namespace][key].value) : metadata[namespace][key].value;
      }
      return '';
    }
  }));

  /*
    * Delievery countdown: used in push cart and on product details
    */
  Alpine.data('countdown', () => ({
    intervalId: null,
    days: '',
    text: '',
    showSaturday: false,

    init() {
      this.tick();
      this.intervalId = window.setInterval(() => this.tick(), 60000);
    },

    destroy() {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    },

    tick() {
      const now = new Date();
      const cutoff = new Date();
      const arrival = new Date();
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const hourCutoff = 16;
      const dayOfWeek = now.getDay();
      let extraDay = 0;

      this.showSaturday = dayOfWeek === 4 || dayOfWeek === 5;

      if (now.getHours() >= hourCutoff || dayOfWeek === 0 || dayOfWeek === 6) {
        extraDay = dayOfWeek === 5 ? 2 : (dayOfWeek === 6 ? 1 : 0);
        arrival.setDate(now.getDate() + 2 + extraDay);
        cutoff.setDate(now.getDate() + 1 + extraDay);
      } else {
        arrival.setDate(now.getDate() + 1);
        cutoff.setDate(now.getDate());
      }

      cutoff.setHours(hourCutoff, 0, 0, 0);

      const difference = Math.max(0, (cutoff.getTime() - now.getTime()) / 1000);
      const wholeDays = Math.floor(difference / 86400);
      const hours = Math.floor(difference / 3600) % 24;
      const minutes = String(Math.floor((difference % 3600) / 60)).padStart(2, '0');
      const dayPrefix = wholeDays > 0 ? `${wholeDays} days ` : '';
      const arrivalLabel = cutoff.getDate() === now.getDate()
        ? 'tomorrow'
        : dayNames[arrival.getDay()];

      this.days = arrivalLabel;
      this.text = `Order within ${dayPrefix}${hours} hrs ${minutes} mins`;
    }
  }));

  const createSimpleCountdown = (targetDate, showSeconds, includeDays = true) => ({
    targetDate,
    showSeconds,
    intervalId: null,
    text: '',

    init() {
      this.tick();
      this.intervalId = window.setInterval(() => this.tick(), 1000);
    },

    destroy() {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    },

    tick() {
      const difference = Math.max(0, (new Date(this.targetDate).getTime() - Date.now()) / 1000);
      const days = Math.floor(difference / 86400);
      const hours = Math.floor(difference / 3600) % 24;
      const totalMinutes = Math.floor(difference / 60);
      const minutes = String(Math.floor((difference % 3600) / 60)).padStart(2, '0');
      const seconds = String(Math.floor(difference % 60)).padStart(2, '0');

      if (this.showSeconds) {
        this.text = `${totalMinutes} mins ${seconds} sec`;
      } else if (includeDays) {
        this.text = `${days} days ${hours} hrs ${minutes} mins`;
      } else {
        this.text = `${hours} hrs ${minutes} mins ${seconds} sec`;
      }
    }
  });

  Alpine.data('simpleCountdown', (targetDate, showSeconds) =>
    createSimpleCountdown(targetDate, showSeconds, true)
  );

  Alpine.data('secondCountdown', (targetDate, showSeconds) =>
    createSimpleCountdown(targetDate, showSeconds, false)
  );

  /*
   * App view + DigitalGenius loader
   */
Alpine.store('appView', {
  isApp: false,

  dgLoaded: false,
  dgLoadPromise: null,
  dgIdleHandle: null,
  dgIntentHandler: null,

  init() {
    const params =
      new URLSearchParams(
        window.location.search
      );

    const salesChannel =
      params.get('sales_channel');

    if (
      salesChannel === 'venn_apps'
    ) {
      sessionStorage.setItem(
        'isApp',
        'true'
      );
    }

    this.isApp =
      sessionStorage.getItem(
        'isApp'
      ) === 'true';

    // Apply app-specific styling before returning.
    document.documentElement.classList.toggle(
      'app-view',
      this.isApp
    );

    if (this.isApp) {
      const mainContent =
        document.getElementById(
          'MainContent'
        );

      if (mainContent) {
        mainContent.style.paddingTop =
          '0px';
      }

      return;
    }

    if (
      !window.FAFThemeConfig
        ?.digitalGeniusEnabled
    ) {
      return;
    }

    this.queueDigitalGenius();
  },


  queueDigitalGenius() {
    if (
      this.dgLoaded ||
      this.dgLoadPromise
    ) {
      return;
    }

    const load = () => {
      this.cancelDigitalGeniusQueue();
      this.loadDigitalGenius();
    };

    this.dgIntentHandler = load;

    window.addEventListener(
      'pointerdown',
      load,
      {
        once: true,
        passive: true
      }
    );

    window.addEventListener(
      'keydown',
      load,
      { once: true }
    );

    window.addEventListener(
      'touchstart',
      load,
      {
        once: true,
        passive: true
      }
    );

    if (
      'requestIdleCallback' in window
    ) {
      this.dgIdleHandle =
        window.requestIdleCallback(
          load,
          { timeout: 4000 }
        );
    } else {
      this.dgIdleHandle =
        window.setTimeout(
          load,
          2500
        );
    }
  },

  cancelDigitalGeniusQueue() {
    if (this.dgIntentHandler) {
      window.removeEventListener(
        'pointerdown',
        this.dgIntentHandler
      );

      window.removeEventListener(
        'keydown',
        this.dgIntentHandler
      );

      window.removeEventListener(
        'touchstart',
        this.dgIntentHandler
      );

      this.dgIntentHandler = null;
    }

    if (this.dgIdleHandle !== null) {
      if (
        'cancelIdleCallback' in window
      ) {
        window.cancelIdleCallback(
          this.dgIdleHandle
        );
      } else {
        window.clearTimeout(
          this.dgIdleHandle
        );
      }

      this.dgIdleHandle = null;
    }
  },

  loadDigitalGenius() {
    if (this.dgLoaded) {
      return Promise.resolve(true);
    }

    if (this.dgLoadPromise) {
      return this.dgLoadPromise;
    }

    this.dgLoadPromise =
      new Promise((resolve, reject) => {
        const initialise = () => {
          if (
            window.dgchat &&
            !window.dgchat.initialized
          ) {
            window.dgchat.init();
            window.dgchat.initialized =
              true;
          }

          this.dgLoaded = true;
          resolve(true);
        };

        if (window.dgchat) {
          initialise();
          return;
        }

        const existing =
          document.querySelector(
            'script[data-faf-digital-genius]'
          );

        const script =
          existing ||
          document.createElement(
            'script'
          );

        script.addEventListener(
          'load',
          initialise,
          { once: true }
        );

        script.addEventListener(
          'error',
          () => {
            reject(
              new Error(
                'DigitalGenius could not be loaded.'
              )
            );
          },
          { once: true }
        );

        if (!existing) {
          script.src =
            'https://chat.digitalgenius.com/init.js';

          script.async = true;

          script.dataset
            .fafDigitalGenius = 'true';

          document.head.appendChild(
            script
          );
        }
      })
      .catch((error) => {
        this.dgLoadPromise = null;
        this.dgLoaded = false;

        console.warn(error.message);

        return false;
      });

    return this.dgLoadPromise;
  }
});


/* Wishlist state and presentation are registered by wishlist.js. */

function createSwipeState() {
  return {
    startX: 0,
    dragStartOffset: 0,
    deltaX: 0,
    maxOffset: 118,
    revealed: false,

    onStart(e) {
      const t = e.touches?.[0] || e;
      this.startX = t.clientX;
      this.dragStartOffset = this.revealed
        ? -this.maxOffset
        : 0;
    },

    onMove(e) {
      const t = e.touches?.[0] || e;
      const diff = t.clientX - this.startX;
      const target =
        this.dragStartOffset + diff;

      this.deltaX = Math.max(
        -this.maxOffset,
        Math.min(0, target)
      );
    },

    onEnd() {
      if (
        this.deltaX <
        -this.maxOffset / 2
      ) {
        this.revealed = true;
        this.deltaX = -this.maxOffset;
      } else {
        this.closeReveal();
      }
    },

    toggleReveal() {
      this.revealed = !this.revealed;
      this.deltaX = this.revealed
        ? -this.maxOffset
        : 0;
    },

    closeReveal() {
      this.revealed = false;
      this.deltaX = 0;
    }
  };
}



Alpine.data('cartLineItem', (config = {}) => ({
  ...createPushCartItem(config.key, config.quantity),
  ...createSwipeState(),

  productHandle: config.productHandle || null,
  productId: config.productId || 0,
  productLimit: Math.max(0, Number(config.productLimit || 0)),
  variantId: Number(config.variantId || 0),
  wishlistOverlayVisible: false,
  wishlistOverlayMessage: '',
  wishlistOverlayTimer: null,
  title: config.title || '',
  vendor: config.vendor || '',
  sku: config.sku || '',
  category: config.category || '',
  priceMinor: Number(config.priceMinor || 0),
  compareAtMinor: Number(config.compareAtMinor || 0),
  url: config.url || '',
  image: config.image || '',
  variantTitle: config.variantTitle || '',
  price: config.price || '',
  colour: config.colour || '',
  style: config.style || '',
  quantityMessage: '',
  quantityMessageTimer: null,


  canIncreaseQuantity() {
    return this.$store.cart.validateLineQuantity(this.key, Number(this.quantity) + 1).allowed;
  },

  increaseQuantity() {
    const validation = this.$store.cart.validateLineQuantity(this.key, Number(this.quantity) + 1);

    if (!validation.allowed) {
      clearTimeout(this.quantityMessageTimer);
      this.quantityMessage = validation.error?.message || 'The maximum quantity is already in your bag.';
      this.quantityMessageBold = true;
      this.quantityMessageTimer = setTimeout(() => {
        this.quantityMessage = '';
        this.quantityMessageBold = false;
      }, 3000);
      return;
    }

    this.setQuantity(Number(this.quantity) + 1);
  },

  interacted: false,

  showWishlistOverlay(message) {
    this.wishlistOverlayMessage = message;
    this.wishlistOverlayVisible = true;

    clearTimeout(this.wishlistOverlayTimer);

    this.wishlistOverlayTimer = setTimeout(() => {
      this.wishlistOverlayVisible = false;
    }, 1400);
  },

  analyticsProduct() {
  return {
    productId:
      this.productId,

    variantId:
      this.variantId,

    sku:
      this.sku,

    title:
      this.title,

    vendor:
      this.vendor,

    category:
      this.category,

    variantTitle:
      this.variantTitle,

    priceMinor:
      this.priceMinor,

    compareAtMinor:
      this.compareAtMinor,

    image:
      this.image,

    url:
      this.url,

    list:
      'cart_drawer'
  };
},


moveToWishlist() {
  const wishlist =
    this.$store.wishlist;

  if (
    !wishlist ||
    !this.productHandle ||
    this.disabled
  ) {
    return;
  }

  if (
    wishlist.hasProduct(
      this.productHandle
    )
  ) {
    const removed =
      wishlist.remove(
        this.productHandle
      );

    if (!removed) return;

    window.FAFAnalytics?.custom(
      'faf_remove_from_wishlist',
      {
        product_id:
          String(
            this.productId
          ),
        variant_id:
          String(
            this.variantId
          ),
        source:
          'cart_drawer',
        reason:
          'heart_toggle'
      }
    );

    this.interacted = true;

    this.showWishlistOverlay(
      'Removed from wishlist'
    );

    this.closeReveal();

    return;
  }

  const added =
    wishlist.add(
      this.productHandle,
      this.variantId
    );

  if (!added) {
    window.FAFAnalytics?.custom(
      'faf_wishlist_limit_reached',
      {
        limit: 50,
        source: 'cart_drawer'
      }
    );

    this.showWishlistOverlay(
      'Wishlist full'
    );

    return;
  }

  window.FAFAnalytics
    ?.addToWishlist(
      this.analyticsProduct(),
      {
        source:
          'cart_drawer'
      }
    );

  this.disabled = true;
  this.closeReveal();

  this.$store.cart.change(
    {
      id: this.key,
      quantity: 0,
      sections: 'push-cart,cart-count'
    },
    (error) => {
      this.disabled = false;

      if (error) {
        this.showWishlistOverlay(
          'Saved to wishlist, but still in bag'
        );
        return;
      }

      this.interacted = true;
      this.showWishlistOverlay(
        'Moved to wishlist'
      );
    }
  );
}
}));



  Alpine.data('confidenceBuilder', (slideCount) => ({
    currentSlide: 0,
    slideCount: slideCount,
    interval: '',
    init() {
      this.$data.createInterval();
    },
    createInterval() {
      this.$data.interval = setInterval(() => {
        this.setSlide(this.$data.currentSlide + 1);
      }, 5000);
    },
    setSlide(index, resetInterval = false) {
      this.$data.currentSlide = index % this.$data.slideCount;
      if(resetInterval) {
        this.$data.resetInterval();
      }
    },
    resetInterval() {
      clearInterval(this.$data.interval);
      this.$data.createInterval();
    }
  }));


  Alpine.data('gifImage', (slideCount) => ({
    currentSlide: 0,
    slideCount: slideCount,
    interval: '',
    init() {
      this.$data.createInterval();
    },
    createInterval() {
      this.$data.interval = setInterval(() => {
        this.setSlide(this.$data.currentSlide + 1);
      }, 2000);
    },
    setSlide(index, resetInterval = false) {
      this.$data.currentSlide = index % this.$data.slideCount;
      if(resetInterval) {
        this.$data.resetInterval();
      }
    },
    resetInterval() {
      clearInterval(this.$data.interval);
      this.$data.createInterval();
    }
  }));

  Alpine.data('productSync', (updates) => ({
    updates: updates,
    init() {
      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: this.updates + '&sections=push-cart,cart-count'
      })
        .then(response => { return response.json();})
        .then(response => {
          if (response.status) {
            alert(response.status);
            return;
          }

          applyCartResponse(response);
        });
    }
  }));

  Alpine.data('newsletter', (endpoint) => ({
    endpoint: endpoint,
    error: '',
    sent: false,
    submit() {
      if(!this.$refs.newsletterForm.reportValidity()) {
        return;
      }

      let formData = new FormData(this.$refs.newsletterForm);
      let formPostData = new URLSearchParams(formData).toString();

      fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formPostData
      })
        .then(response => { return response.json();})
        .then(response => {
          this.sent = true;
        }).catch((error) => {
          this.error = error;
          setTimeout(() => this.error = '', 5000);
        });

    }
  }));


  Alpine.data('quickAddAlt', (config = {}) => ({
    quickAddToBag: false,
    variantId: '',
    loading: false,
    adding: false,
    added: false,
    error: '',
    productId: Number(config.productId || 0),
    productLimit: Number(config.productLimit || 0),
    variantInventory: config.variants || {},

    init() {
      Alpine.store('cart')?.registerProductRule(
        this.productId,
        this.productLimit,
        this.variantInventory
      );
    },

    configureProduct(nextConfig = {}) {
      if (nextConfig.productId) {
        this.productId = Number(nextConfig.productId);
      }

      if (nextConfig.productLimit !== undefined) {
        this.productLimit = Number(nextConfig.productLimit || 0);
      }

      if (nextConfig.variants) {
        this.variantInventory = {
          ...this.variantInventory,
          ...nextConfig.variants
        };
      }

      Alpine.store('cart')?.registerProductRule(
        this.productId,
        this.productLimit,
        this.variantInventory
      );
    },

    quantityInCart(variantId) {
      return Alpine.store('cart').quantityFor(variantId);
    },

    quantityForProduct() {
      if (!this.productId) return 0;
      return Alpine.store('cart').quantityForProduct(this.productId);
    },

    inventoryLimitForVariant(variantId) {
      const inventory = this.variantInventory[String(Number(variantId || 0))];
      if (!inventory) return null;

      if (!inventory.tracksInventory || inventory.inventoryPolicy === 'continue') {
        return null;
      }

      return Math.max(0, Number(inventory.inventoryQuantity || 0));
    },

    isProductAtLimit() {
      return this.productLimit > 0 && this.quantityForProduct() >= this.productLimit;
    },

    isVariantAtLimit(variantId) {
      const numericVariantId = Number(variantId || 0);
      const inventory = this.variantInventory[String(numericVariantId)];

      if (!inventory || !inventory.available) return true;
      if (this.isProductAtLimit()) return true;

      const inventoryLimit = this.inventoryLimitForVariant(numericVariantId);
      if (inventoryLimit === null) return false;

      return this.quantityInCart(numericVariantId) >= inventoryLimit;
    },

    allVariantsAtLimit() {
      if (this.isProductAtLimit()) return true;

      const variantIds = Object.keys(this.variantInventory);
      return variantIds.length > 0 && variantIds.every((variantId) => this.isVariantAtLimit(variantId));
    },

    add(variantId) {
      const numericVariantId = Number(variantId || 0);
      if (!numericVariantId || this.loading || this.isVariantAtLimit(numericVariantId)) return;

      this.loading = true;
      this.adding = true;
      this.error = '';
      this.variantId = numericVariantId;

      Alpine.store('cart').addVariant(
        numericVariantId,
        (error) => {
          this.loading = false;
          this.adding = false;

          if (error) {
            this.error = error.message || 'This item could not be added. Please try again.';
            return;
          }

          this.added = true;
          setTimeout(() => {
            this.added = false;
            this.variantId = '';
          }, 2000);
        },
        null,
        null,
        'quick',
        {
          productId: this.productId,
          productLimit: this.productLimit,
          inventoryLimit: this.inventoryLimitForVariant(numericVariantId),
          source: 'collection_quick_add'
        }
      );
    },

    variantClass(variantId) {
      if (this.isVariantAtLimit(variantId)) {
        return 'border-sg-grey-2 text-sg-grey-4 line-through cursor-not-allowed';
      }

      if (this.variantId == variantId) {
        if (this.adding) return 'bg-sg-navy text-white opacity-60 cursor-wait';
        if (this.added) return 'bg-sg-gold text-white';
      }

      return 'text-sg-navy hover:bg-sg-navy hover:text-white';
    }
  }));

  /*
   * Freee gift choice popup
   */
  Alpine.data('offerPopup', () => ({
    showOffer: true,
    setOptOut() {
      return fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            opt_out_keyring: 1
          }
        })
      })
        .then(async (response) => {
          const payload = await response.json().catch(() => ({}));

          if (!response.ok || payload.status) {
            throw new Error(
              payload.description ||
              payload.message ||
              'Your preference could not be saved.'
            );
          }

          applyCartResponse(payload);
          return payload;
        })
        .catch((error) => {
          console.error('Offer opt-out failed:', error);
          return null;
        });
    }
  }));

  Alpine.data('simpleDotScroller', (slideCount) => ({
    scrollPercent: 0,
    dotIndex: 1,
    slideCount: slideCount,
    updateDots() {
      this.$data.scrollPercent = this.$refs.slider.scrollLeft / this.$refs.slider.scrollWidth;
      this.$data.dotIndex = Math.round(this.$data.scrollPercent * this.$data.slideCount) + 1;
    },
    scrollTo(index) {
      this.$refs.slider.scrollTo({ left: ((index - 1) / this.slideCount) * this.$refs.slider.scrollWidth, behavior: 'smooth' });
    }
  }));

  Alpine.data('wishlistOpener', () => ({
    open() {
      var start = Date.now();
      var timeout;

      window.location = 'fairfaxandfavor://wishlist';

      timeout = setTimeout(function () {
        if (Date.now() - start < 3000) {
          window.location.href = 'https://apps.apple.com/gb/app/fairfax-and-favor-2/id6743029857';
        }
      }, 2000);
    }
  }));

  Alpine.data('accountPanel', (config = {}) => ({
    activeView: 'overview',
    viewHistory: [],
    notificationOpen: false,
    statusMessage: '',
    statusType: 'success',
    statusTimer: null,
    loggedIn: config.loggedIn === true || config.loggedIn === 'true',

    init() {
      if (config.profile && this.$store.profile) {
        this.$store.profile.init(config.profile);
      }

      const queryView = new URLSearchParams(window.location.search).get('tab');
      const requestedView = this.$store.lazyPanel?.requestedAccountView || queryView;
      const allowedViews = ['overview', 'orders', 'details', 'contact', 'benefits'];

      if (
        allowedViews.includes(requestedView) &&
        (this.loggedIn || requestedView === 'overview')
      ) {
        this.activeView = requestedView;

        if (requestedView !== 'overview') {
          this.$nextTick(() => {
            window.dispatchEvent(new CustomEvent('account-tab:open', {
              detail: { tab: requestedView }
            }));
            this.focusHeading();
          });
        }
      }
    },

    get viewTitle() {
      return {
        overview: 'Account',
        orders: 'Your orders',
        details: 'Your details',
        benefits: 'Loyalty benefits',
        contact: 'Contact us'
      }[this.activeView] || 'Account';
    },

    get profileCompleteCount() {
      if (!this.$store.profile) return 0;

      return [
        Boolean(this.$store.profile.birthday),
        Boolean(this.$store.profile.appLoggedIn),
        Boolean(this.$store.profile.sms_sub),
        Boolean(this.$store.profile.emailSub)
      ].filter(Boolean).length;
    },

    get profilePercentage() {
      return Math.round((this.profileCompleteCount / 4) * 100);
    },

    switchView(view, options = {}) {
      const allowedViews = ['overview', 'orders', 'contact', 'details', 'benefits'];

      if (!allowedViews.includes(view) || view === this.activeView) {
        return;
      }

      if (!options.replace) {
        this.viewHistory.push(this.activeView);
      }

      this.activeView = view;
      this.notificationOpen = false;

      if (view !== 'overview') {
        window.dispatchEvent(new CustomEvent('account-tab:open', {
          detail: { tab: view }
        }));
      }

      if (this.$store.lazyPanel?.open !== false) {
        this.$nextTick(() => {
          this.scrollActiveViewTop();
          this.focusHeading();
        });
      }
    },

    goBack() {
      const previousView = this.viewHistory.pop() || 'overview';
      this.switchView(previousView, { replace: true });
    },

    scrollActiveViewTop() {
      const scrollRegion = this.$refs[`${this.activeView}Scroll`];

      if (scrollRegion) {
        scrollRegion.scrollTop = 0;
      }
    },

    focusHeading() {
      requestAnimationFrame(() => {
        this.$refs.viewHeading?.focus({ preventScroll: true });
      });
    },

    showStatus(message, type = 'success', duration = 3500) {
      window.clearTimeout(this.statusTimer);
      this.statusMessage = message;
      this.statusType = type;

      this.statusTimer = window.setTimeout(() => {
        this.statusMessage = '';
      }, duration);
    },

    handleProfileSaved(event) {
      this.viewHistory = [];
      this.activeView = 'overview';
      this.showStatus(event.detail?.message || 'Your details have been saved.');

      this.$nextTick(() => {
        this.scrollActiveViewTop();
        this.focusHeading();
      });
    },

    async copyText(value, successMessage = 'Copied') {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(value);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = value;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          textarea.remove();
        }

        this.showStatus(successMessage);
      } catch (error) {
        this.showStatus('Unable to copy. Please select the code manually.', 'error', 5000);
      }
    },

    openWishlist() {
      this.notificationOpen = false;
      this.activeView = 'overview';
      this.viewHistory = [];
      this.$store.cart.mode = 'wishlist';
      this.$store.lazyPanel.openPanel('push-cart');
    },

    closePanel() {
      this.$store.lazyPanel.closePanel();
    },

    closeAll() {
      this.closePanel();
    },

    reset() {
      this.activeView = 'overview';
      this.viewHistory = [];
      this.notificationOpen = false;
      this.statusMessage = '';
      this.$store.lazyPanel.requestedAccountView = 'overview';
      window.clearTimeout(this.statusTimer);
    },

    destroy() {
      window.clearTimeout(this.statusTimer);
    }
  }));
  

  /**
   * Customer form - handles customer details update
   */
Alpine.data('customerForm', (config) => ({
  // --- guard ---
  _inited: false,
  _geoLoaded: false,
  _phoneLibPromise: null,

  // --- view / behaviour control ---
  context: config.context || 'account',
  webhookUrl: config.webhookUrl || 'https://webhooks.mechanic.dev/9a9bd1ef-0f7e-4a83-8b57-e22f32d5c634',
  view: config.view || 'form',
  loading: false,

  firstName: config.firstName || '',
  lastName: config.lastName || '',
  phone: config.phone || '',
  store: config.store || '',
  customerPhone: config.customerPhone || '',
  email: config.email || '',
  subscribeMarketing: config.subscribeMarketing === true || config.subscribeMarketing === 'true',
  subscribeSMSMarketing: config.subscribeSMSMarketing === true || config.subscribeSMSMarketing === 'true',
  birthday: config.birthday || '',
  initialSubscribeMarketing: config.subscribeMarketing === true || config.subscribeMarketing === 'true',
  initialSubscribeSMSMarketing: config.subscribeSMSMarketing === true || config.subscribeSMSMarketing === 'true',
  birthdayDay: '',
  birthdayMonth: '',
  declinePlatinum: config.declinePlatinum || '',
  address1: config.address1 || '',
  address2: config.address2 || '',
  city: config.city || '',
  country: config.country || '',
  zip: config.zip || '',
  feedbackForm: config.feedbackForm || '',
  clubPage: config.clubPage === true || config.clubPage === 'true',
  appLoggedIn: config.appLoggedIn === true || config.appLoggedIn === 'true',
  error: '',
  submitted: false,
  showSaved: false,
  optOut: config.optOut === true || config.optOut === 'true',
  optOutConfirmed: config.optOutConfirmed === true || config.optOutConfirmed === 'true',
  countryCode: config.countryCode || '',
  addressId: config.addressId || '',
  customerId: config.customerId || '',
  token: config.token || '',
  countryCodeForSubmit: config.countryCodeForSubmit || '',

  monthNames: [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ],

  async initOnce() {
    if (this._inited) return;
    this._inited = true;

    if (this.birthday && this.birthday.includes('-')) {
      const [month, day] = this.birthday.split('-');
      this.birthdayMonth = month;
      this.birthdayDay = day;

      if (this.birthday === localStorage.getItem('pendingBirthday')) {
        localStorage.removeItem('pendingBirthday');
      }
    } else {
      const storedBirthday = localStorage.getItem('pendingBirthday');

      if (storedBirthday && storedBirthday.includes('-')) {
        this.birthday = storedBirthday;
        const [month, day] = storedBirthday.split('-');
        this.birthdayMonth = month;
        this.birthdayDay = day;
      }
    }

    await this.loadGeoCountryOnce();
  },

  async loadGeoCountryOnce() {
    if (this._geoLoaded) return;
    this._geoLoaded = true;

    if (this.countryCode) return;

    this.countryCode =
      window.FAFThemeConfig?.countryCode ||
      window.Shopify?.country ||
      '';
  },

  ordinal(day) {
    if ([11, 12, 13].includes(day)) return day + 'th';

    switch (day % 10) {
      case 1:
        return day + 'st';
      case 2:
        return day + 'nd';
      case 3:
        return day + 'rd';
      default:
        return day + 'th';
    }
  },

  formatBirthday(birthday) {
    if (!birthday || !birthday.includes('-')) return '';

    const [month, day] = birthday.split('-');
    const monthName = this.monthNames[parseInt(month) - 1];

    return `${monthName} ${this.ordinal(parseInt(day))}`;
  },

  loadLibPhoneNumber() {
    if (window.libphonenumber?.parsePhoneNumber) {
      return Promise.resolve(window.libphonenumber);
    }

    if (this._phoneLibPromise) {
      return this._phoneLibPromise;
    }

    this._phoneLibPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://unpkg.com/libphonenumber-js@1.10.21/bundle/libphonenumber-max.js';
      s.async = true;
      s.onload = () => resolve(window.libphonenumber);
      s.onerror = reject;
      document.head.appendChild(s);
    });

    return this._phoneLibPromise;
  },

  async isPhoneValid() {
    const fallbackValid = /^[+()\d\s.-]{7,24}$/.test(this.phone || '');

    try {
      const lib = await this.loadLibPhoneNumber();
      const phoneNumber = lib.parsePhoneNumber(this.phone || '', this.countryCode);

      return phoneNumber.isValid();
    } catch (e) {
      return fallbackValid;
    }
  },

  async submitForm() {
    if (this.loading) return;

    this.error = '';
    this.loading = true;

    let newBirthday = '';

    const day = this.birthdayDay;
    const month = this.birthdayMonth;

    if (day && month) {
      newBirthday = month.padStart(2, '0') + '-' + day.padStart(2, '0');
    } else if (day || month) {
      this.error = 'Please select both month and day for your birthday.';
      this.loading = false;
      return;
    }

    if (!this.email || !this.email.includes('@')) {
      this.error = 'Please enter a valid email address.';
      this.loading = false;
      return;
    }

    if (this.subscribeSMSMarketing && !this.phone) {
      this.error = 'Please add a phone number to receive SMS updates.';
      this.loading = false;
      return;
    }

    if (this.phone && !(await this.isPhoneValid())) {
      this.error = 'Please enter a valid phone number.';
      this.loading = false;
      return;
    }

    try {
      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: this.customerId,
          address_id: this.addressId,
          first_name: this.firstName,
          last_name: this.lastName,
          phone: this.phone,
          store: this.store,
          app_logged_in: this.appLoggedIn,
          customer_phone: this.customerPhone,
          address1: this.address1,
          address2: this.address2,
          declinePlatinum: this.declinePlatinum,
          city: this.city,
          feedback_form: this.feedbackForm,
          country: this.country,
          context: this.context,
          zip: this.zip,
          email: this.email,
          club_page: this.clubPage,
          subscribe_marketing: this.subscribeMarketing,
          subscribe_sms_marketing: this.subscribeSMSMarketing,
          opt_out: this.optOut,
          birthday: newBirthday || this.birthday || '',
          token: this.token,
          country_code: this.countryCodeForSubmit
        }),
      });

      if (!response.ok) {
        const responseMessage = await response.text().catch(() => '');
        throw new Error(responseMessage || 'We could not save your details.');
      }
      if (
          !this.initialSubscribeMarketing &&
          this.subscribeMarketing
        ) {
          window.FAFAnalytics?.subscribe({
            leadType: 'email',
            email:
              this.email
          });
        }

        if (
          !this.initialSubscribeSMSMarketing &&
          this.subscribeSMSMarketing
        ) {
          window.FAFAnalytics?.subscribe({
            leadType: 'phone',
            email:
              this.email,
            phone:
              this.phone
          });
        }

        this.initialSubscribeMarketing =
          this.subscribeMarketing;

        this.initialSubscribeSMSMarketing =
          this.subscribeSMSMarketing;

      this.submitted = true;
      this.optOutConfirmed = this.optOut;

      if (newBirthday) {
        this.birthday = newBirthday;
        localStorage.setItem('pendingBirthday', newBirthday);
      }

      Alpine.store('profile').birthday = newBirthday || this.birthday || '';
      Alpine.store('profile').sms_sub = this.subscribeSMSMarketing;
      Alpine.store('profile').emailSub = this.subscribeMarketing;

      Alpine.store('profile').showProgress =
        !(
          Alpine.store('profile').birthday &&
          Alpine.store('profile').sms_sub &&
          Alpine.store('profile').emailSub &&
          Alpine.store('profile').appLoggedIn
        );

      if (this.context === 'platinumOnboarding') {
        this.view = 'success';
      } else {
        this.showSaved = true;
        window.dispatchEvent(new CustomEvent('account-form:saved', {
          detail: { message: 'Your details have been saved.' }
        }));

        window.setTimeout(() => {
          this.showSaved = false;
        }, 2000);
      }
    } catch (e) {
      this.error = e?.message || 'Something went wrong. Please try again.';
    } finally {
      this.loading = false;
    }
  }
}));

  /*
   * Account panel - show progress for app signup etc
   */
Alpine.data('profileProgress', () => ({
  get birthday() {
    return Alpine.store('profile').birthday;
  },
  get sms_sub() {
    return Alpine.store('profile').sms_sub;
  },
  get appLoggedIn() {
    return Alpine.store('profile').appLoggedIn;
  },
  get emailSub() {
    return Alpine.store('profile').emailSub;
  },
  get showProgress() {
    return Alpine.store('profile').showProgress;
  },
  set showProgress(value) {
    Alpine.store('profile').showProgress = value;
  },
  get hasBirthday() {
    return this.birthday !== '';
  },
  get completeCount() {
    return [
      this.hasBirthday,
      this.appLoggedIn,
      this.sms_sub,
      this.emailSub
    ].filter(Boolean).length;
  },
  get percentage() {
    return Math.round((this.completeCount / 4) * 100);
  }
}));

Alpine.data('lazyPanelShell', () => ({
  lastFocusedElement: null,
  wasOpen: false,
  focusReturnTimer: null,

  init() {
    this.$watch('$store.lazyPanel.open', (open) => {
      window.clearTimeout(this.focusReturnTimer);

      if (open) {
        if (!this.wasOpen) {
          this.lastFocusedElement = document.activeElement;
        }

        this.wasOpen = true;
        this.$nextTick(() => {
          requestAnimationFrame(() => this.focusPanel());
        });
      } else {
        this.wasOpen = false;

        this.focusReturnTimer = window.setTimeout(() => {
          if (this.lastFocusedElement?.isConnected && this.lastFocusedElement?.focus) {
            this.lastFocusedElement.focus({ preventScroll: true });
          }
        }, 320);
      }
    });

    this.$watch('$store.lazyPanel.activePanel', (activePanel) => {
      if (
        this.$store.lazyPanel.open &&
        !['loading', 'error', ''].includes(activePanel)
      ) {
        this.$nextTick(() => {
          requestAnimationFrame(() => this.focusPanel());
        });
      }
    });
  },

  focusableElements() {
    if (!this.$refs.panel) return [];

    return Array.from(
      this.$refs.panel.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => {
      const style = window.getComputedStyle(element);

      return (
        style.visibility !== 'hidden' &&
        style.display !== 'none' &&
        (element.offsetWidth > 0 || element.offsetHeight > 0)
      );
    });
  },

  focusPanel() {
    const preferred = this.$refs.panel?.querySelector(
      '[data-panel-initial-focus]:not([disabled])'
    );
    const focusable = this.focusableElements();

    (preferred || focusable[0] || this.$refs.panel)?.focus({ preventScroll: true });
  },

  trapFocus(event) {
    if (!this.$store.lazyPanel.open || event.key !== 'Tab') {
      return;
    }

    const focusable = this.focusableElements();

    if (!focusable.length) {
      event.preventDefault();
      this.$refs.panel?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  },

  close() {
    this.$store.lazyPanel.closePanel();
  },

  destroy() {
    window.clearTimeout(this.focusReturnTimer);
  }
}));

  /*
   * Lazy panel - used for cart and account
   */
  Alpine.store('lazyPanel', {
    init() {
      Alpine.effect(() => {
        toggleBodyScroll(this.open);
      });

      const requestedView = new URLSearchParams(window.location.search).get('tab');

      if (['orders', 'details', 'benefits'].includes(requestedView)) {
        window.setTimeout(() => this.openAccountView(requestedView), 50);
      }
    },

    width: 50,
    open: false,
    loadedPanels: [],
    activePanel: '',
    requestedPanel: '',
    status: 'idle',
    error: '',
    label: 'Panel',
    requestId: 0,
    requestedAccountView: '',

    closePanel() {
      this.open = false;
      window.dispatchEvent(new CustomEvent('lazy-panel:closed', {
        detail: { panel: this.activePanel }
      }));
    },

    loadPanel(sectionName, options = {}) {
      const host = document.getElementById('lazy-' + sectionName);
      const alreadyLoaded =
        this.loadedPanels.includes(sectionName) &&
        host &&
        host.childElementCount > 0;

        if (
          alreadyLoaded &&
          !options.force
        ) {
          this.activePanel =
            sectionName;

          this.status =
            'ready';

          this.error =
            '';

          window.dispatchEvent(
            new CustomEvent(
              'lazy-panel:ready',
              {
                detail: {
                  panel:
                    sectionName,

                  cached:
                    true
                }
              }
            )
          );

          return Promise.resolve(
            true
          );
        }

      this.requestedPanel = sectionName;
      this.activePanel = 'loading';
      this.status = 'loading';
      this.error = '';

      const currentRequest = ++this.requestId;
      const separator = window.Shopify.routes.root.includes('?') ? '&' : '?';
      const url = window.Shopify.routes.root + separator + 'sections=' + encodeURIComponent(sectionName);

      return fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('The panel could not be loaded.');
          }

          return response.json();
        })
        .then((response) => {
          if (currentRequest !== this.requestId) {
            return false;
          }

          const didSetMarkup = this.setMarkup(sectionName, response[sectionName]);

          if (!didSetMarkup) {
            throw new Error('The panel content was unavailable.');
          }

          this.activePanel = sectionName;
          this.status = 'ready';
          this.error = '';

          window.dispatchEvent(new CustomEvent('lazy-panel:ready', {
            detail: { panel: sectionName }
          }));

          return true;
        })
        .catch((error) => {
          if (currentRequest !== this.requestId) {
            return false;
          }

          console.error('Lazy panel failed to load:', error);
          this.activePanel = 'error';
          this.status = 'error';
          this.error = error.message || 'The panel could not be loaded.';
          return false;
        });
    },

    setMarkup(sectionName, markup) {
      const host = document.getElementById('lazy-' + sectionName);

      if (!host || !markup) {
        return false;
      }

      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const section = parsed.getElementById('shopify-section-' + sectionName);

      if (!section) {
        return false;
      }

      host.innerHTML = section.innerHTML;

      window.FAFSections?.enhance?.(
        host,
        {
          source: 'lazy-panel',
          panel: sectionName
        }
      );

      if (!this.loadedPanels.includes(sectionName)) {
        this.loadedPanels.push(sectionName);
      }

      return true;
    },

    async openAccountView(view = 'overview') {
      const allowedViews = ['overview', 'orders', 'contact', 'details', 'benefits'];

      if (!allowedViews.includes(view)) {
        return false;
      }

      this.requestedAccountView = view;

      const host = document.getElementById('lazy-push-account');
      const accountLoaded =
        this.loadedPanels.includes('push-account') &&
        host &&
        host.childElementCount > 0;

      if (accountLoaded) {
        window.dispatchEvent(new CustomEvent('account-panel:view', {
          detail: { view }
        }));
      }

      const opened = await this.openPanel('push-account');

      if (!opened) {
        return false;
      }

      if (!accountLoaded) {
        requestAnimationFrame(() => {
          window.dispatchEvent(new CustomEvent('account-panel:view', {
            detail: { view }
          }));
        });
      }

      return true;
    },

    openPanel(sectionName, markup) {
      this.requestedPanel = sectionName;
      this.width = sectionName === 'push-account' ? 50 : 50;
      this.label = sectionName === 'push-account' ? 'Account' : 'Shopping bag';
      this.open = true;

      if (markup) {
        const didSetMarkup = this.setMarkup(sectionName, markup);

        if (didSetMarkup) {
          this.activePanel = sectionName;
          this.status = 'ready';
          this.error = '';
          window.dispatchEvent(new CustomEvent('lazy-panel:ready', {
            detail: { panel: sectionName }
          }));
        } else {
          this.activePanel = 'error';
          this.status = 'error';
          this.error = 'The panel content was unavailable.';
        }

        return Promise.resolve(didSetMarkup);
      }

      return this.loadPanel(sectionName);
    },

    retryPanel() {
      if (!this.requestedPanel) return;
      return this.loadPanel(this.requestedPanel, { force: true });
    }
  });






  Alpine.store('cart', {
    mode: 'cart', // can be wrap
    quantities: {},
    productQuantities: {},
    pendingVariantQuantities: {},
    pendingProductQuantities: {},
    productRules: { ...(window.FAFProductRules?.products || {}) },
    variantRules: { ...(window.FAFProductRules?.variants || {}) },
    linesByKey: {},
    stateRequestId: 0,
    stateLoaded: false,
    statePromise: null,

    init() {
      if (window.FAFCartSeed?.items) {
        this.applyState(window.FAFCartSeed);
      } else {
        this.refreshState();
      }

      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.refreshState();
      });
    },

    applyState(cart) {
      const quantities = {};
      const productQuantities = {};
      const linesByKey = {};

      for (const line of cart?.items || []) {
        const variantId = Number(line.variant_id || line.id || 0);
        const productId = Number(line.product_id || 0);
        const quantity = Number(line.quantity || 0);

        if (variantId) {
          quantities[variantId] = (quantities[variantId] || 0) + quantity;
        }

        if (productId) {
          productQuantities[productId] = (productQuantities[productId] || 0) + quantity;
        }

        const key = String(line.key || '');
        if (key) {
          linesByKey[key] = {
            key,
            variantId,
            productId,
            quantity,
            item: line
          };
        }
      }

      this.quantities = quantities;
      this.productQuantities = productQuantities;
      this.linesByKey = linesByKey;
      this.stateLoaded = true;

      window.dispatchEvent(new CustomEvent('cart:updated', {
        detail: {
          cart,
          quantities: { ...quantities },
          productQuantities: { ...productQuantities }
        }
      }));

      return cart;
    },

    setState(cart) {
      this.stateRequestId += 1;
      return this.applyState(cart);
    },

    refreshState() {
      const requestId = ++this.stateRequestId;

      const request = fetch(window.Shopify.routes.root + 'cart.js', {
        headers: { Accept: 'application/json' }
      })
        .then((response) => {
          if (!response.ok) throw new Error('Unable to refresh the cart state.');
          return response.json();
        })
        .then((cart) => {
          if (requestId !== this.stateRequestId) return cart;
          return this.applyState(cart);
        })
        .catch((error) => {
          console.error('Cart state refresh failed:', error);
          return null;
        })
        .finally(() => {
          if (this.statePromise === request) {
            this.statePromise = null;
          }
        });

      this.statePromise = request;
      return request;
    },

    ensureState() {
      if (this.stateLoaded) return Promise.resolve(true);

      return (this.statePromise || this.refreshState()).then((cart) => {
        if (!cart && !this.stateLoaded) {
          throw new Error('Unable to confirm the current bag quantity. Please try again.');
        }

        return true;
      });
    },

    registerProductRule(productId, productLimit = 0, variants = {}) {
      const numericProductId = Number(productId || 0);
      const numericLimit = Math.max(0, Number(productLimit || 0));

      if (numericProductId) {
        const existingRule = this.productRules[numericProductId] || {};
        const existingLimit = Math.max(0, Number(existingRule.limit || 0));

        this.productRules[numericProductId] = {
          ...existingRule,
          limit: numericLimit > 0 ? numericLimit : existingLimit
        };
      }

      Object.entries(variants || {}).forEach(([variantId, variant]) => {
        const numericVariantId = Number(variantId || 0);
        if (!numericVariantId) return;

        this.variantRules[numericVariantId] = {
          ...(this.variantRules[numericVariantId] || {}),
          ...(variant || {}),
          productId: numericProductId || Number(variant?.productId || 0)
        };
      });
    },

    productLimitFor(productId) {
      return Math.max(0, Number(this.productRules[Number(productId)]?.limit || 0));
    },

    inventoryLimitForVariant(variantId) {
      const rule = this.variantRules[Number(variantId)] || {};

      if (!rule.tracksInventory || rule.inventoryPolicy === 'continue') {
        return null;
      }

      return Math.max(0, Number(rule.inventoryQuantity || 0));
    },

    resolveVariantRule(variantId, constraints = {}) {
      const numericVariantId = Number(variantId || 0);
      const storedRule = this.variantRules[numericVariantId] || {};
      const productId = Number(constraints.productId || storedRule.productId || 0);
      const productLimit = Number(
        constraints.productLimit ||
        storedRule.productLimit ||
        this.productLimitFor(productId) ||
        0
      );

      const hasExplicitInventoryLimit =
        Object.prototype.hasOwnProperty.call(constraints, 'inventoryLimit');

      return {
        productId,
        productLimit: Math.max(0, productLimit),
        inventoryLimit: hasExplicitInventoryLimit
          ? (constraints.inventoryLimit === null ? null : Math.max(0, Number(constraints.inventoryLimit)))
          : this.inventoryLimitForVariant(numericVariantId)
      };
    },

    isProductAtLimit(productId) {
      const limit = this.productLimitFor(productId);
      return limit > 0 && this.quantityForProduct(productId) >= limit;
    },

    lineForIdentifier(identifier) {
      const key = String(identifier || '');
      if (this.linesByKey[key]) return this.linesByKey[key];

      const numericIdentifier = Number(identifier || 0);
      if (!numericIdentifier) return null;

      return Object.values(this.linesByKey).find((line) => line.variantId === numericIdentifier) || null;
    },

    validateLineQuantity(identifier, targetQuantity) {
      const line = this.lineForIdentifier(identifier);
      const quantity = Math.max(0, Number(targetQuantity || 0));

      if (!line || quantity <= line.quantity) {
        return { allowed: true, line };
      }

      const rule = this.resolveVariantRule(line.variantId, { productId: line.productId });
      const nextProductQuantity =
        this.quantityForProduct(line.productId) - line.quantity + quantity;

      if (rule.productLimit > 0 && nextProductQuantity > rule.productLimit) {
        return { allowed: false, line, error: this.createLimitError(rule.productLimit) };
      }

      if (rule.inventoryLimit !== null && quantity > rule.inventoryLimit) {
        const error = new Error('The maximum available quantity is already in your bag.');
        error.code = 'INVENTORY_LIMIT_REACHED';
        return { allowed: false, line, error };
      }

      return { allowed: true, line };
    },

    quantityFor(variantId) {
      return Number(this.quantities[Number(variantId)] || 0);
    },

    quantityForProduct(productId) {
      return Number(this.productQuantities[Number(productId)] || 0);
    },

    pendingQuantityFor(variantId) {
      return Number(this.pendingVariantQuantities[Number(variantId)] || 0);
    },

    pendingQuantityForProduct(productId) {
      return Number(this.pendingProductQuantities[Number(productId)] || 0);
    },

    reserveQuantity(variantId, productId, quantity = 1) {
      const numericVariantId = Number(variantId || 0);
      const numericProductId = Number(productId || 0);
      const numericQuantity = Math.max(0, Number(quantity || 0));

      if (numericVariantId && numericQuantity) {
        this.pendingVariantQuantities[numericVariantId] = this.pendingQuantityFor(numericVariantId) + numericQuantity;
      }

      if (numericProductId && numericQuantity) {
        this.pendingProductQuantities[numericProductId] = this.pendingQuantityForProduct(numericProductId) + numericQuantity;
      }
    },

    releaseQuantity(variantId, productId, quantity = 1) {
      const numericVariantId = Number(variantId || 0);
      const numericProductId = Number(productId || 0);
      const numericQuantity = Math.max(0, Number(quantity || 0));

      if (numericVariantId && numericQuantity) {
        const remainingVariant = Math.max(0, this.pendingQuantityFor(numericVariantId) - numericQuantity);
        if (remainingVariant) {
          this.pendingVariantQuantities[numericVariantId] = remainingVariant;
        } else {
          delete this.pendingVariantQuantities[numericVariantId];
        }
      }

      if (numericProductId && numericQuantity) {
        const remainingProduct = Math.max(0, this.pendingQuantityForProduct(numericProductId) - numericQuantity);
        if (remainingProduct) {
          this.pendingProductQuantities[numericProductId] = remainingProduct;
        } else {
          delete this.pendingProductQuantities[numericProductId];
        }
      }
    },

    createLimitError(limit) {
      const numericLimit = Number(limit || 0);
      const error = new Error(
        numericLimit === 1
          ? 'You can only add 1 of this item to your bag.'
          : `You can only add ${numericLimit} of this item to your bag.`
      );
      error.code = 'PRODUCT_LIMIT_REACHED';
      return error;
    },

    trackAddedToCart(addedItem, fallbackVariantId, fallbackQuantity = 1) {
      if (!addedItem || typeof addedItem !== 'object') return;

      const image = addedItem.featured_image?.url || addedItem.image || '';
      const relativeUrl = addedItem.url || '';
      const productUrl = relativeUrl
        ? new URL(relativeUrl, window.location.origin).href
        : window.location.href;
      const price = Number(addedItem.final_price ?? addedItem.price ?? 0) / 100;
      const lineTotal = Number(
        addedItem.final_line_price
        ?? addedItem.original_line_price
        ?? ((addedItem.final_price ?? addedItem.price ?? 0) * Number(addedItem.quantity || fallbackQuantity))
      ) / 100;

      window._learnq = window._learnq || [];
      window._learnq.push(['track', 'Added to Cart', {
        Name: addedItem.product_title || addedItem.title || '',
        ProductID: Number(addedItem.product_id || 0) || null,
        VariantID: Number(addedItem.variant_id || addedItem.id || fallbackVariantId || 0) || null,
        VariantName: addedItem.variant_title || '',
        SKU: addedItem.sku || '',
        Categories: addedItem.product_type ? [addedItem.product_type] : [],
        ImageURL: image,
        URL: productUrl,
        Brand: addedItem.vendor || '',
        Price: price,
        Quantity: Number(addedItem.quantity || fallbackQuantity),
        $value: lineTotal
      }]);
    },

    addVariantWithDiscount(
      variantId,
      discountCode,
      options = {}
    ) {
      return fetch(
        window.Shopify.routes.root +
          'cart/update.js',
        {
          method:
            'POST',

          headers: {
            'Content-Type':
              'application/json',

            Accept:
              'application/json'
          },

          body:
            JSON.stringify({
              discount:
                discountCode,

              attributes: {
                _discount:
                  discountCode
              }
            })
        }
      )
        .then(async (response) => {
          const payload =
            await response
              .json()
              .catch(() => ({}));

          if (
            !response.ok ||
            payload.status
          ) {
            throw new Error(
              payload.description ||
              payload.message ||
              'The discount could not be applied.'
            );
          }

          return new Promise(
            (resolve, reject) => {
              Alpine.store('cart')
                .addVariant(
                  variantId,
                  (error) => {
                    if (error) {
                      reject(error);
                      return;
                    }

                    resolve(true);
                  },
                  options.properties ||
                    null,
                  options.extraItems ||
                    null,
                  options.mode ||
                    null,
                  (() => {
                    const addConstraints = {
                      productId: Number(options.productId || 0),
                      productLimit: Number(options.productLimit || 0),
                      source: options.source || 'discount_add'
                    };

                    if (
                      Object.prototype.hasOwnProperty.call(
                        options,
                        'inventoryLimit'
                      )
                    ) {
                      addConstraints.inventoryLimit =
                        options.inventoryLimit;
                    }

                    return addConstraints;
                  })()
                );
            }
          );
        });
    },

    /*
    addVariantWithDiscount(variantId, discountCode) {
      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ discount: discountCode, attributes: { '_discount': discountCode } })
      })
      .then(response => response.json())
      .then(data => {
        Alpine.store('cart').addVariant(variantId);
      });
    },
  */
    autoRemoveDiscount(variantId) {
      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ attributes: { '_discount': '' } })
      })
      .then(response => response.json())
      .then(data => {
        Alpine.store('cart').change({ id: variantId, quantity: 0, sections: 'push-cart' });
      });
    },

    addVariant(variantId, onComplete, properties, extraItems, mode, constraints = {}) {
      const numericVariantId = Number(variantId || 0);
      const resolvedRule = this.resolveVariantRule(numericVariantId, constraints);
      const productId = resolvedRule.productId;
      const productLimit = resolvedRule.productLimit;
      const inventoryLimit = resolvedRule.inventoryLimit;
      const requestedQuantity = 1;

      const finish = (error = null) => {
        if (!onComplete) return;

        try {
          onComplete(error || undefined);
        } catch (callbackError) {
          console.error('Add-to-Bag completion callback failed:', callbackError);
        }
      };

      if (!Number.isFinite(numericVariantId) || numericVariantId <= 0) {
        const error = new Error('A valid product variant was not selected.');

        if (onComplete) {
          finish(error);
        } else {
          alert(error.message);
        }

        return;
      }

      const addToCart = () => {
        const productQuantity = productId
          ? this.quantityForProduct(productId) + this.pendingQuantityForProduct(productId)
          : 0;
        const variantQuantity = this.quantityFor(numericVariantId) + this.pendingQuantityFor(numericVariantId);

        if (productLimit > 0 && productQuantity + requestedQuantity > productLimit) {
          const error = this.createLimitError(productLimit);
          if (onComplete) finish(error); else alert(error.message);
          return;
        }

        if (inventoryLimit !== null && variantQuantity + requestedQuantity > inventoryLimit) {
          const error = new Error('The maximum available quantity is already in your bag.');
          error.code = 'INVENTORY_LIMIT_REACHED';
          if (onComplete) finish(error); else alert(error.message);
          return;
        }

        this.reserveQuantity(numericVariantId, productId, requestedQuantity);

        const items = [{
          id: numericVariantId,
          quantity: requestedQuantity
        }];

        if (Array.isArray(extraItems)) {
          items.push(...extraItems);
        }

        if (properties) {
          items[0].properties = properties;
        }
        const sellingPlanId =
          String(
            constraints.sellingPlanId || ''
          ).trim();

        if (sellingPlanId) {
          items[0].selling_plan =
            sellingPlanId;
        }

        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items,
            sections: 'push-cart,cart-count'
          })
        })
          .then(async (response) => {
            const payload = await response.json().catch(() => ({}));

            if (!response.ok || payload.status) {
              throw new Error(
                payload.description ||
                payload.message ||
                (Number(payload.status) === 422
                  ? 'Not enough stock'
                  : 'This item could not be added to your Bag.')
              );
            }

            return payload;
          })
          .then(async (payload) => {
            const refreshedCart = await this.refreshState();

            if (!refreshedCart) {
              this.quantities[numericVariantId] = this.quantityFor(numericVariantId) + requestedQuantity;

              if (productId) {
                this.productQuantities[productId] = this.quantityForProduct(productId) + requestedQuantity;
              }

              this.stateLoaded = true;
              window.dispatchEvent(new CustomEvent('cart:updated', {
                detail: {
                  cart: null,
                  quantities: { ...this.quantities },
                  productQuantities: { ...this.productQuantities }
                }
              }));
            }

            this.releaseQuantity(numericVariantId, productId, requestedQuantity);

            const sections = payload.sections || {};
            const pushCartMarkup = sections['push-cart'];
            const cartCountMarkup = sections['cart-count'];
            const cartCount = document.querySelector('.js-cart-count');

            if (cartCount && cartCountMarkup) {
              cartCount.innerHTML = cartCountMarkup;
              cartCount.classList.remove('hidden');
            }

            const addedItems = Array.isArray(payload.items)
              ? payload.items
              : (payload && typeof payload === 'object' ? [payload] : []);
            const addedItem = addedItems.find((line) =>
              Number(line.variant_id || line.id || 0) === numericVariantId
            ) || addedItems[0] || null;

            this.trackAddedToCart(addedItem, numericVariantId, requestedQuantity);

            if (
              window.FAFThemeConfig
                ?.elevarManualCartEvents
            ) {
              const trackedItems =
                addedItems.length > 0
                  ? addedItems
                  : addedItem
                    ? [addedItem]
                    : [];

              trackedItems.forEach((item) => {
                window.FAFAnalytics?.addToCart(
                  item,
                  {
                    productId:
                      Number(
                        item.product_id ||
                        productId ||
                        0
                      ),
                    variantId:
                      Number(
                        item.variant_id ||
                        item.id ||
                        numericVariantId
                      ),
                    quantity:
                      Number(
                        item.quantity ||
                        requestedQuantity
                      ),
                    source:
                      constraints.source ||
                      mode ||
                      'storefront'
                  }
                );
              });
            }

            if (mode === 'wishlist') {
              this.mode = 'wishlist';
              finish();

              if (pushCartMarkup) {
                setTimeout(() => {
                  Alpine.store('lazyPanel').setMarkup('push-cart', pushCartMarkup);
                }, 650);
              }

              return;
            }

            if (mode === 'quick') {
              if (pushCartMarkup) {
                Alpine.store('lazyPanel').setMarkup('push-cart', pushCartMarkup);
              }

              window.dispatchEvent(new CustomEvent('cart-added', {
                detail: {
                  timeout: 3200,
                  item: Array.isArray(payload.items) ? payload.items[0] : null
                },
                bubbles: true
              }));

              finish();
              return;
            }

            this.mode = mode || 'cart';

            if (pushCartMarkup) {
              Alpine.store('lazyPanel').openPanel('push-cart', pushCartMarkup);
            } else {
              Alpine.store('lazyPanel').openPanel('push-cart');
            }

            finish();
          })
          .catch((error) => {
            this.releaseQuantity(numericVariantId, productId, requestedQuantity);

            if (onComplete) {
              finish(error);
            } else {
              alert(error.message || error);
            }
          });
      };

      if ((productLimit > 0 || inventoryLimit !== null) && !this.stateLoaded) {
        this.ensureState()
          .then(addToCart)
          .catch((error) => {
            if (onComplete) finish(error); else alert(error.message || error);
          });
      } else {
        addToCart();
      }
    },

    change(formData, onComplete) {
      let completed = false;

      const finish = (error = null) => {
        if (completed) return;
        completed = true;

        if (onComplete) {
          try {
            onComplete(error || undefined);
          } catch (callbackError) {
            console.error('Cart-change completion callback failed:', callbackError);
          }
        } else if (error) {
          alert(error.message || String(error));
        }
      };
      const previousLine =
        this.lineForIdentifier(
          formData?.id
        );

      const previousItem =
        previousLine?.item || null;

      const previousQuantity =
        Number(
          previousLine?.quantity || 0
        );

      if (Object.prototype.hasOwnProperty.call(formData || {}, 'quantity')) {
        const validation = this.validateLineQuantity(formData.id, formData.quantity);
        if (!validation.allowed) {
          finish(validation.error);
          return Promise.resolve(null);
        }
      }

      return fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(formData)
      })
        .then(async (response) => {
          const payload = await response.json().catch(() => ({}));
          if (!response.ok || payload.status) {
            throw new Error(payload.message || payload.description || 'The bag could not be updated.');
          }
          return payload;
        })
        .then((response) => {
              if (
                window.FAFThemeConfig
                  ?.elevarManualCartEvents &&
                previousItem &&
                Object.prototype
                  .hasOwnProperty.call(
                    formData || {},
                    'quantity'
                  )
              ) {
                const nextItem =
                  (response.items || []).find(
                    (line) =>
                      String(
                        line.key || ''
                      ) ===
                      String(
                        previousLine?.key || ''
                      )
                  ) || null;

                const nextQuantity =
                  Number(
                    nextItem?.quantity ??
                    formData.quantity ??
                    previousQuantity
                  );

                const quantityDelta =
                  nextQuantity -
                  previousQuantity;

                if (quantityDelta > 0) {
                  window.FAFAnalytics
                    ?.addToCart(
                      nextItem ||
                        previousItem,
                      {
                        quantity:
                          quantityDelta,
                        source:
                          'cart_drawer'
                      }
                    );
                }

                if (quantityDelta < 0) {
                  window.FAFAnalytics
                    ?.removeFromCart(
                      previousItem,
                      {
                        quantity:
                          Math.abs(
                            quantityDelta
                          ),
                        source:
                          'cart_drawer'
                      }
                    );
                }
              }

              this.setState(response);

          if(response.sections?.['push-cart']) {
            Alpine.store('lazyPanel').openPanel('push-cart', response.sections['push-cart']);
          }

          finish();
          return response;

        })
        .catch((error) => {
          finish(error);
          return null;
        });
    }
  });

  Alpine.data('pointsCalculator', (customerId, customerEmail, qrFillColor, containerId) => ({
    qrCode: false,
    qrGenerated: false,
    toggleQRCode() {
      this.qrCode = !this.qrCode;
      if (this.qrCode && !this.qrGenerated) {
        this.$nextTick(() => {
          this.generateQR();
        });
      }
    },
    async generateQR() {
      const container = document.getElementById(containerId);
      if (!container || this.qrGenerated) return;

      const qrLink = `https://jeri.link/fairfax-and-favor-2/mgm?referral=${customerEmail}&customer_id=${customerId}`;

      try {
        await window.FAFQRCode.render(container, {
          text: qrLink,
          width: 150,
          height: 150,
          colorDark: qrFillColor,
          colorLight: 'transparent',
          correctLevel: 'L'
        });
        this.qrGenerated = true;
      } catch (error) {
        console.warn('Unable to render loyalty QR code.', error);
      }
    }
  }));

  Alpine.data('pointsAnimation', () => ({
    counter: 0,
    count(finalCount) {
      const duration = 2500;
      const start = 0;
      const end = finalCount;
      const range = end - start;
      let startTime = null;

      const step = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        this.counter = Math.floor(start + range * progress);
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          this.counter = end;
        }
      };
      requestAnimationFrame(step);
    }
  }));

  Alpine.store('appInfo', {
    getUrl() {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      if (/android/i.test(ua)) {
        return window.routes.app_android;
      } else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
        return window.routes.app_ios;
      }

      return window.routes.app_default;
    },
    openApp() {
      const start = Date.now();
      window.location = 'fairfaxandfavor://home';
      setTimeout(() => {
        if (Date.now() - start < 3000) {
          const ua = navigator.userAgent || navigator.vendor || window.opera;
          if (/android/i.test(ua)) {
            window.location.href = window.routes.app_android;
          } else {
            window.location.href = 'https://apps.apple.com/gb/app/fairfax-and-favor-2/id6743029857';
          }
        }
      }, 2000);
    }
  });

  Alpine.store('recent', {
    keep: 12,
    recentHandle: 'recently-viewed-6',

    normaliseHandle(value) {
      if (typeof value !== 'string') return '';

      const handle = value.trim().toLowerCase();

      return /^[\p{L}\p{N}_][\p{L}\p{M}\p{N}_-]*$/u.test(handle)
        ? handle
        : '';
    },

    readHandles() {
      try {
        const stored = JSON.parse(
          window.localStorage.getItem(this.recentHandle) || '[]'
        );

        if (!Array.isArray(stored)) return [];

        const handles = [];
        const seen = new Set();

        for (const value of stored) {
          const handle = this.normaliseHandle(value);

          if (!handle || seen.has(handle)) continue;

          seen.add(handle);
          handles.push(handle);

          if (handles.length >= this.keep) break;
        }

        return handles;
      } catch (error) {
        // Recently viewed is optional; do not reset other customer storage.
        return [];
      }
    },

    async getRecent(config = {}) {
      const {
        targetMarkupId,
        view,
        remoteMarkupId = 'remote-recs',
        onComplete = null,
        limit = 8
      } = config || {};

      const target = typeof targetMarkupId === 'string'
        ? document.getElementById(targetMarkupId)
        : null;

      if (!target) return null;

      const requestedLimit = Number(limit);
      const resultLimit = Number.isFinite(requestedLimit)
        ? Math.min(this.keep, Math.max(0, Math.floor(requestedLimit)))
        : Math.min(8, this.keep);

      const handles = this.readHandles().slice(0, resultLimit);

      if (!handles.length) {
        target.classList.add('hidden');
        return null;
      }

      let renderedTarget;

      try {
        const searchPath = window.routes?.search;

        if (
          typeof searchPath !== 'string' || !searchPath ||
          typeof view !== 'string' || !view.trim()
        ) {
          target.classList.add('hidden');
          return null;
        }

        const params = new URLSearchParams({
          view: view.trim(),
          q: handles.map((handle) => `handle:${handle}`).join(' OR ')
        });

        renderedTarget = await sectionReplace(
          `${searchPath}.json?${params.toString()}`,
          targetMarkupId,
          remoteMarkupId
        );

        if (!renderedTarget) {
          target.classList.add('hidden');
          return null;
        }

        renderedTarget.classList.remove('hidden');
      } catch (error) {
        target.classList.add('hidden');
        console.warn('Recently viewed products could not be loaded:', error);
        return null;
      }

      // Analytics failure must not hide a successfully rendered rail.
      try {
        await window.FAFAnalytics?.viewItemList?.(
          renderedTarget,
          { list: 'recently_viewed' }
        );
      } catch (error) {
        console.warn('Recently viewed analytics could not be recorded:', error);
      }

      try {
        if (typeof onComplete === 'function') await onComplete();
      } catch (error) {
        console.warn('Recently viewed completion callback failed:', error);
      }

      return renderedTarget;
    },

    push(value) {
      const handle = this.normaliseHandle(value);
      if (!handle) return false;

      const handles = this.readHandles().filter((item) => item !== handle);
      handles.unshift(handle);

      try {
        window.localStorage.setItem(
          this.recentHandle,
          JSON.stringify(handles.slice(0, this.keep))
        );

        return true;
      } catch (error) {
        // Keep failure local; no fallback writes to cookies or other keys.
        return false;
      }
    }
  });

  Alpine.store('tagalysRecs', {
    failure(config = {}, reason = 'adapter_unavailable') {
      const result = {
        status: 'error',
        reason,
        context: typeof config.context === 'string' ? config.context : 'product',
        target: null,
        count: 0,
        retryable: true
      };

      try {
        if (typeof config.onSettled === 'function') {
          Promise.resolve(config.onSettled(result)).catch(console.warn);
        }
      } catch (error) {
        console.warn('Recommendation completion callback failed:', error);
      }

      return result;
    },

    async getRecsForProduct(config = {}) {
      try {
        if (typeof window.FAFRecommendations?.loadProduct !== 'function') {
          await window.FAFAssets.loadScript(
            'recommendations',
            window.FAFThemeConfig?.assets?.recommendations
          );
        }

        if (typeof window.FAFRecommendations?.loadProduct !== 'function') {
          return this.failure(config);
        }

        return window.FAFRecommendations.loadProduct(config);
      } catch (error) {
        console.warn('Recommendation adapter failed to load:', error);
        return this.failure(config, 'adapter_load_failed');
      }
    }
  });


  Alpine.data('verticalRecs', (id) => ({
    id: id,
    progress: 5,
    showBar: false,
    init() {
      Alpine.store('tagalysRecs').getRecsForProduct({
        productId: this.id,
        targetMarkupId: 'cart-recs',
        onComplete: () => {
          this.$nextTick(() => {
            this.$refs.verticalSlider.addEventListener('scroll', () => this.update(), { passive: true });
            setTimeout(() => this.update(), 1000);
          });
        }
      });
    },
    update() {
      const el = this.$refs.verticalSlider;
      if (!el) return;
      const max = el.scrollHeight - el.clientHeight;
      this.showBar = max > 2; // show only if scrollable
      this.progress = this.showBar ? (el.scrollTop / max) * 100 : 0;
    }
  }));


});


document.addEventListener('alpine:initialized', () => {
  const params = new URLSearchParams(window.location.search);

  if (params.get('cart-pop') !== '1') {
    return;
  }

  /*
   * Wait until Alpine has finished initialising and the browser
   * has completed a stable layout before the scroll lock measures
   * and fixes the site header.
   */
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const cartStore = Alpine.store('cart');
      const panelStore = Alpine.store('lazyPanel');

      if (!cartStore || !panelStore) {
        return;
      }

      cartStore.mode = 'cart';
      panelStore.openPanel('push-cart');
    });
  });
});

function applyCartResponse(response = {}) {
  if (window.FAFSections?.applyCartSections) {
    window.FAFSections.applyCartSections(response);
    return;
  }

  Alpine.store('cart')?.setState?.(response);
  refreshCartMarkup(response.sections || {});
}

function refreshCartMarkup(sections = {}) {
  if (window.FAFSections?.applyCartSections) {
    window.FAFSections.applyCartSections({ sections });
    return;
  }

  if (sections['push-cart']) {
    const target = document.getElementById('shopify-section-push-cart');
    const parsed = new DOMParser().parseFromString(
      sections['push-cart'],
      'text/html'
    );
    const source = parsed.getElementById('shopify-section-push-cart');

    if (target && source) {
      target.innerHTML = source.innerHTML;
    }
  }

  if (sections['cart-count']) {
    const count = document.querySelector('.js-cart-count');

    if (count) {
      count.innerHTML = sections['cart-count'];
    }
  }
}

function getSizedImage(src, size) {
  return src.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, '.')
    .replace(/\.jpg|\.png|\.gif|\.jpeg/g, function(match) {
      return '_' + size + match;
    });
}

function setClipboard(value) {
    var tempInput = document.createElement("input");
    tempInput.style = "position: absolute; left: -1000px; top: -1000px";
    tempInput.value = value;
    document.body.appendChild(tempInput);
    tempInput.select();
    document.execCommand("copy");
    document.body.removeChild(tempInput);
}

function onTagalysReady(callback) {
  if (typeof callback !== 'function') {
    return Promise.resolve(false);
  }

  if (!onTagalysReady.pending) {
    onTagalysReady.pending = new Promise((resolve) => {
      const deadline = Date.now() + 10000;

      const poll = () => {
        const sdk = window.Tagalys;

        try {
          if (
            typeof sdk?.setConfiguration === 'function' &&
            typeof sdk?.setPlatformConfiguration === 'function'
          ) {
            if (onTagalysReady.configuredSdk !== sdk) {
              setTagalysConfiguration();
              setPlatformConfigurationForTagalys();
              onTagalysReady.configuredSdk = sdk;
            }

            if (
              typeof sdk.APIConnector?.SearchResults?.new === 'function' &&
              typeof sdk.APIConnector?.SearchSuggestions?.new === 'function' &&
              typeof sdk.APIConnector?.Recommendations?.call === 'function'
            ) {
              resolve(sdk);
              return;
            }
          }
        } catch (error) {
          console.warn('Tagalys initialisation failed.', error);
          resolve(null);
          return;
        }

        if (Date.now() >= deadline) {
          resolve(null);
          return;
        }

        window.setTimeout(poll, 100);
      };

      poll();
    }).then((sdk) => {
      // Allow a later caller to retry after an unsuccessful wait.
      if (!sdk) onTagalysReady.pending = null;
      return sdk;
    });
  }

  return onTagalysReady.pending.then((sdk) => {
    if (!sdk) return false;

    try {
      callback();
      return true;
    } catch (error) {
      // One failed consumer must not stop other queued consumers.
      console.warn('Tagalys ready callback failed.', error);
      return false;
    }
  });
}

function setTagalysConfiguration() {
  const config = window.FAFThemeConfig || {};

  Tagalys.setConfiguration({
    api: {
      serverUrl: "https://api-r4.tagalys.com",
      credentials: {
        clientCode: "E87983FB80DC6BD2",
        apiKey: "cdb824442afeb6f9c190bcf0491265bd"
      },
      storeId: "2716001"
    },
    locale: config.locale || document.documentElement.lang || "en-GB",
    currency: {
      displayFormatter: "{{currencyLabel}}{{value}}",
      code: config.currencyCode || window.Shopify?.currency?.active || "GBP",
      label: config.currencyLabel || "",
      fractionalDigits: 2,
      forceFractionalDigits: true
    },
    analyticsStorageConsentProvided: function() {
      try {
        return Boolean(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.());
      } catch (error) {
        return false;
      }
    },
    track: true
  });
}

function setPlatformConfigurationForTagalys() {
  Tagalys.setPlatformConfiguration({
    platform: "Shopify",
    metafields: {
      products: [
        {
          namespace: "prod_strings",
          key: "colour",
        },
        {
          namespace: "prod_strings",
          key: "range_title",
        },
        {
          namespace: "prod_strings",
          key: "fit",
        },
        {
          namespace: "faf",
          key: "product_style",
        },
      ],
    },
    countryCode: window.FAFThemeConfig?.countryCode || window.Shopify?.country || "GB",
    baseCountryCode: window.FAFThemeConfig?.baseCountryCode || "GB",
    useStorefrontAPIForSecondaryMarkets: true,
    waitForStorefrontAPI: true,
    storefrontAPI: {
      accessToken: "b568fff0945e995e4ab87b6f5cce6250",
      myShopifyDomain: "fairfax-and-favor-2.myshopify.com",
    },
  })
}

    function sectionReplace(
      url,
      onPageTargetID,
      remoteTargetID
    ) {
      return fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(
          `Content could not be loaded (${response.status}).`
        );
      }

      return response.text();
    })
    .then((response) => {
      const target = document.getElementById(onPageTargetID);
      const parsed = new DOMParser().parseFromString(response, 'text/html');
      const source = parsed.getElementById(remoteTargetID);

      if (!target || !source) return;

      target.innerHTML = source.innerHTML;

      window.FAFSections?.enhance?.(
        target,
        {
          source: 'section-replace',
          remoteTargetID
        }
      );
      return target;
    });
}
